import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import localforage from 'localforage';
import { Calendar, Clock, Copy, Trash2, Users, FileSpreadsheet, ChevronDown, ChevronUp, 
         CheckSquare, PlusCircle, Search, Zap, Repeat, Smartphone, XCircle, Save, 
         FileText, Edit3, Settings, AlertCircle, Upload, Download, File  } from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';

// Enhanced color palette with more accent colors
const colors = {
  sunriseRed: '#DA291C',
  sunriseRedLight: '#EC4B3E',
  sunriseRedDark: '#B92218',
  greyDusk: '#3D3935',
  greyDuskLight: '#5D5955',
  greyDuskDark: '#2D2925',
  paleDawn: '#E6E3DF',
  paleDawnLight: '#F6F3EF',
  paleDawnDark: '#D6D3CF',
  iceWhite: '#FFFFFF',
  textPrimary: '#3D3935',
  textSecondary: '#5D5955',
  success: '#26A69A',
  warning: '#FFA000',
  error: '#E53935',
  info: '#42A5F5',
  accent1: '#7E57C2',
  accent2: '#26C6DA',
  accent3: '#66BB6A',
  boxShadow: '0 8px 16px rgba(61, 57, 53, 0.08)'
};

// Enhanced animations
const animations = {
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  slideIn: 'slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards',
  fadeIn: 'fadeIn 0.3s ease forwards',
  slideUp: 'slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards',
  pulse: 'pulse 1.5s infinite',
  slideInRight: 'slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
  popIn: 'popIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
  rotate: 'rotate 1s linear infinite'
};

// Comprehensive styles with more components
const styles = {
  // Global container
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(61, 57, 53, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    animation: animations.fadeIn,
    overflow: 'hidden'
  },
  formContainer: {
    width: '90%',
    maxWidth: '1000px',
    maxHeight: '90vh',
    backgroundColor: colors.iceWhite,
    borderRadius: '16px',
    boxShadow: '0 24px 48px rgba(61, 57, 53, 0.2)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: animations.slideIn
  },
  drawerContainer: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.iceWhite,
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    boxShadow: '0 -8px 24px rgba(61, 57, 53, 0.2)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: animations.slideUp,
    zIndex: 1100,
    maxHeight: '90vh'
  },
  header: {
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${colors.paleDawn}`,
    position: 'relative'
  },
  headerTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: colors.textPrimary,
    margin: 0
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: colors.textSecondary,
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: animations.transition,
    '&:hover': {
      backgroundColor: colors.paleDawn,
      color: colors.textPrimary
    }
  },

  // Progress indicator
  progressContainer: {
    padding: '24px 32px 50px',
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
  },
  progressBar: {
    position: 'absolute',
    top: '34px',
    left: '32px',
    right: '32px',
    height: '2px',
    backgroundColor: colors.paleDawn,
    zIndex: 0
  },
  progressFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    backgroundColor: colors.sunriseRed,
    transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  progressStep: {
    width: '36px',
    top: "-7px",
    height: '36px',
    borderRadius: '50%',
    backgroundColor: colors.paleDawn,
    border: `2px solid ${colors.paleDawnDark}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    fontSize: '14px',
    color: colors.textSecondary,
    position: 'relative',
    zIndex: 1,
    transition: animations.transition
  },
  progressStepCompleted: {
    backgroundColor: colors.sunriseRedLight,
    borderColor: colors.sunriseRed,
    color: colors.iceWhite
  },
  progressStepActive: {
    backgroundColor: colors.sunriseRed,
    borderColor: colors.sunriseRed,
    color: colors.iceWhite,
    transform: 'scale(1.1)',
    boxShadow: '0 4px 8px rgba(218, 41, 28, 0.3)'
  },
  progressStepLabel: {
    position: 'absolute',
    top: '40px',
    left: '50%',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap',
    fontSize: '12px',
    fontWeight: '500',
    color: colors.textSecondary,
    transition: animations.transition
  },
  progressStepActiveLabel: {
    color: colors.sunriseRed,
    fontWeight: '600'
  },

  // Form content
  formContent: {
    padding: '24px 32px',
    overflowY: 'auto',
    flexGrow: 1,
    maxHeight: 'calc(90vh - 220px)'
  },
  section: {
    marginBottom: '150px',
    animation: animations.fadeIn,
    animationDuration: '0.4s'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center'
  },
  formGroup: {
    marginBottom: '24px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    fontSize: '14px',
    color: colors.textSecondary
  },
  required: {
    color: colors.error,
    marginLeft: '4px'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: colors.iceWhite,
    border: `1px solid ${colors.paleDawn}`,
    borderRadius: '8px',
    fontSize: '16px',
    color: colors.textPrimary,
    transition: animations.transition,
    outline: 'none',
    '&:focus': {
      borderColor: colors.sunriseRed,
      boxShadow: '0 0 0 3px rgba(218, 41, 28, 0.1)'
    },
    '&:hover:not(:focus)': {
      borderColor: colors.paleDawnDark
    }
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: colors.iceWhite,
    border: `1px solid ${colors.paleDawn}`,
    borderRadius: '8px',
    fontSize: '16px',
    color: colors.textPrimary,
    minHeight: '120px',
    resize: 'vertical',
    transition: animations.transition,
    lineHeight: '1.5',
    outline: 'none',
    '&:focus': {
      borderColor: colors.sunriseRed,
      boxShadow: '0 0 0 3px rgba(218, 41, 28, 0.1)'
    },
    '&:hover:not(:focus)': {
      borderColor: colors.paleDawnDark
    }
  },
  errorText: {
    color: colors.error,
    fontSize: '14px',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },

  // Grid layout
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '0 -12px'
  },
  col: {
    padding: '0 12px',
    flexBasis: '100%',
    maxWidth: '100%'
  },
  col6: {
    padding: '0 12px',
    flexBasis: '50%',
    maxWidth: '50%'
  },
  col4: {
    padding: '0 12px',
    flexBasis: '33.333333%',
    maxWidth: '33.333333%'
  },
  col3: {
    padding: '0 12px',
    flexBasis: '25%',
    maxWidth: '25%'
  },

  // Card elements
  card: {
    backgroundColor: colors.iceWhite,
    borderRadius: '12px',
    boxShadow: `0 2px 8px rgba(61, 57, 53, 0.08)`,
    padding: '24px',
    marginBottom: '24px',
    marginTop: "20px",
    border: `1px solid ${colors.paleDawn}`,
    transition: animations.transition,
    position: 'relative', // For the autosave indicator
    '&:hover': {
      boxShadow: `0 4px 12px rgba(61, 57, 53, 0.12)`,
    }
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    padding: '0 0 16px 0',
    borderBottom: `1px solid ${colors.paleDawn}`
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: colors.textPrimary,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  // Buttons
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 32px',
    borderTop: `1px solid ${colors.paleDawn}`,
    gap: '12px',
    position: 'sticky',
    bottom: 0,
    backgroundColor: colors.iceWhite,
    zIndex: 10,
    boxShadow: '0 -4px 12px rgba(61, 57, 53, 0.05)'
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: animations.transition,
    gap: '8px'
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  primaryButton: {
    backgroundColor: colors.sunriseRed,
    color: colors.iceWhite,
    '&:hover': {
      backgroundColor: colors.sunriseRedLight,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(218, 41, 28, 0.3)'
    },
    '&:active': {
      backgroundColor: colors.sunriseRedDark,
      transform: 'translateY(0)',
      boxShadow: 'none'
    }
  },
  secondaryButton: {
    backgroundColor: colors.paleDawn,
    color: colors.textPrimary,
    '&:hover': {
      backgroundColor: colors.paleDawnLight,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(61, 57, 53, 0.1)'
    },
    '&:active': {
      backgroundColor: colors.paleDawnDark,
      transform: 'translateY(0)',
      boxShadow: 'none'
    }
  },
  dangerButton: {
    backgroundColor: colors.error,
    color: colors.iceWhite,
    '&:hover': {
      opacity: 0.9,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(229, 57, 53, 0.3)'
    }
  },
  successButton: {
    backgroundColor: colors.success,
    color: colors.iceWhite,
    '&:hover': {
      opacity: 0.9,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(38, 166, 154, 0.3)'
    }
  },
  iconButton: {
    width: '36px',
    height: '36px',
    padding: '0',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    backgroundColor: colors.paleDawn,
    border: 'none',
    cursor: 'pointer',
    transition: animations.transition,
    color: colors.textSecondary,
    '&:hover': {
      backgroundColor: colors.paleDawnDark,
      color: colors.textPrimary,
      transform: 'scale(1.1)'
    }
  },
  addButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '8px',
    backgroundColor: colors.sunriseRed,
    color: colors.iceWhite,
    border: 'none',
    cursor: 'pointer',
    transition: animations.transition,
    gap: '8px',
    width: '100%',
    marginTop: '16px',
    '&:hover': {
      backgroundColor: colors.sunriseRedLight,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(218, 41, 28, 0.3)'
    },
    '&:active': {
      backgroundColor: colors.sunriseRedDark,
      transform: 'translateY(0)',
      boxShadow: 'none'
    }
  },

  // Switch/Toggle
  switchContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  switchInput: {
    position: 'absolute',
    opacity: 0,
    width: 0,
    height: 0
  },
  switchSlider: {
    position: 'relative',
    display: 'inline-block',
    width: '44px',
    height: '24px',
    backgroundColor: colors.paleDawn,
    borderRadius: '34px',
    marginRight: '12px',
    transition: animations.transition,
    cursor: 'pointer'
  },
  switchActive: {
    backgroundColor: colors.sunriseRed
  },
  switchLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: colors.textPrimary
  },

  // Enhanced Schedule specific styles
  scheduleContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '10px 0',
  },
  scheduleItem: {
    borderRadius: '12px',
    border: `1px solid ${colors.paleDawn}`,
    backgroundColor: colors.iceWhite,
    overflow: 'hidden',
    marginBottom: '16px',
    boxShadow: '0 2px 4px rgba(61, 57, 53, 0.05)',
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)'
  },
  scheduleItemExpanded: {
    boxShadow: '0 4px 16px rgba(61, 57, 53, 0.1)',
    borderColor: colors.sunriseRed,
    transform: 'scale(1.01)'
  },
  scheduleHeader: {
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: animations.transition,
    backgroundColor: colors.iceWhite
  },
  scheduleHeaderExpanded: {
    backgroundColor: colors.paleDawnLight,
    borderBottom: `1px solid ${colors.paleDawn}`
  },
  scheduleTitle: {
    fontWeight: '600',
    fontSize: '16px',
    color: colors.textPrimary,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  scheduleTitleExpanded: {
    color: colors.sunriseRed
  },
  scheduleDate: {
    fontSize: '14px',
    color: colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  scheduleContent: {
    overflow: 'hidden',
    transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    maxHeight: '0',
    opacity: 0,
    padding: '0 20px'
  },
  scheduleContentExpanded: {
    padding: '20px',
    maxHeight: '2000px',
    opacity: 1
  },
  scheduleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '16px'
  },
  
  // Schedule badges
  badgeContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: '500',
    gap: '6px'
  },
  groupBadge: {
    backgroundColor: 'rgba(126, 87, 194, 0.1)',
    color: colors.accent1,
  },
  repeatBadge: {
    backgroundColor: 'rgba(102, 187, 106, 0.1)',
    color: colors.accent3,
  },
  allUsersBadge: {
    backgroundColor: 'rgba(66, 165, 245, 0.1)',
    color: colors.info,
  },
  
  // NEW: Time range slider
  timeSliderContainer: {
    width: '100%',
    padding: '24px 12px 12px',
    position: 'relative',
    marginBottom: '20px',
  },
  timeSlider: {
    height: '60px',
    backgroundColor: colors.paleDawnLight,
    borderRadius: '8px',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  timeSliderMarks: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 10px',
  },
  timeSliderMark: {
    height: '100%',
    width: '1px',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    position: 'relative',
  },
  timeSliderMarkLabel: {
    position: 'absolute',
    top: '-20px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '10px',
    color: colors.textSecondary,
    whiteSpace: 'nowrap',
  },
  timeSliderRange: {
    position: 'absolute',
    height: '100%',
    backgroundColor: 'rgba(218, 41, 28, 0.2)',
    border: '2px solid rgba(218, 41, 28, 0.5)',
    top: 0,
  },
  timeSliderHandle: {
    width: '12px',
    height: '60px',
    backgroundColor: colors.sunriseRed,
    position: 'absolute',
    top: 0,
    cursor: 'ew-resize',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: colors.iceWhite,
    fontSize: '14px',
    fontWeight: 'bold',
    zIndex: 5,
  },
  timeSliderHandleStart: {
    borderRadius: '8px 0 0 8px',
    left: 0,
  },
  timeSliderHandleEnd: {
    borderRadius: '0 8px 8px 0',
    right: 0,
  },
  timeSliderTime: {
    position: 'absolute',
    bottom: -24,
    fontSize: '12px',
    fontWeight: 'medium',
    color: colors.textPrimary,
    marginTop: '4px',
    padding: '2px 4px',
    backgroundColor: colors.iceWhite,
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    zIndex: 5,
  },
  timeSliderTimeStart: {
    left: 0,
    transform: 'translateX(-50%)',
  },
  timeSliderTimeEnd: {
    right: 0,
    transform: 'translateX(50%)',
  },
  
  // Enhanced time selector
  timeSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  timeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
    marginTop: '8px',
    maxHeight: '160px',
    overflowY: 'auto',
    padding: '4px',
    borderRadius: '8px',
    border: `1px solid ${colors.paleDawn}`,
  },
  timeButton: {
    padding: '8px',
    backgroundColor: colors.iceWhite,
    border: `1px solid ${colors.paleDawn}`,
    borderRadius: '6px',
    cursor: 'pointer',
    transition: animations.transition,
    '&:hover': {
      backgroundColor: colors.paleDawnLight,
    }
  },
  timeButtonActive: {
    backgroundColor: colors.sunriseRedLight,
    color: colors.iceWhite,
    borderColor: colors.sunriseRed,
  },
  
  // Enhanced group selector with member list
  groupSelector: {
    marginTop: '16px',
    marginBottom: '20px',
  },
  groupTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  groupOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '12px',
  },
  groupOption: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: colors.paleDawnLight,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: animations.transition,
    '&:hover': {
      backgroundColor: colors.paleDawn,
    }
  },
  groupOptionActive: {
    backgroundColor: 'rgba(218, 41, 28, 0.1)',
    borderLeft: `4px solid ${colors.sunriseRed}`,
  },
  groupCount: {
    fontSize: '12px',
    padding: '4px 8px',
    borderRadius: '12px',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    marginLeft: 'auto',
  },
  groupMembersList: {
    maxHeight: '0',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    padding: '0 16px',
  },
  groupMembersListExpanded: {
    maxHeight: '200px',
    padding: '12px 16px',
    overflow: 'auto',
    borderTop: `1px solid ${colors.paleDawn}`,
    marginTop: '8px',
  },
  groupMember: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 0',
    fontSize: '13px',
  },
  groupMemberAvatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: colors.iceWhite,
  },
  
  // Enhanced file upload section
  fileUploadContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    borderRadius: '8px',
    border: `2px dashed ${colors.paleDawn}`,
    backgroundColor: colors.paleDawnLight,
    cursor: 'pointer',
    transition: animations.transition,
    marginBottom: '20px',
    '&:hover': {
      borderColor: colors.sunriseRed,
      backgroundColor: 'rgba(218, 41, 28, 0.05)',
    }
  },
  fileUploadActive: {
    borderColor: colors.sunriseRed,
    backgroundColor: 'rgba(218, 41, 28, 0.05)',
  },
  fileUploadText: {
    fontSize: '14px',
    fontWeight: '500',
    color: colors.textPrimary,
    marginTop: '12px',
    textAlign: 'center'
  },
  filePreview: {
    width: '100%',
    borderRadius: '8px',
    border: `1px solid ${colors.paleDawn}`,
    marginTop: '16px',
    overflow: 'hidden'
  },
  filePreviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: colors.paleDawnLight,
    borderBottom: `1px solid ${colors.paleDawn}`,
  },
  filePreviewContent: {
    maxHeight: '200px',
    overflowY: 'auto',
    padding: '12px',
    backgroundColor: colors.iceWhite,
  },
  fileTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  fileTableHead: {
    backgroundColor: colors.paleDawnLight,
    fontSize: '12px',
    fontWeight: '600',
  },
  fileTableCell: {
    padding: '8px 12px',
    borderBottom: `1px solid ${colors.paleDawn}`,
    fontSize: '12px',
  },
  
  // AI Suggestion UI
  aiSuggestionContainer: {
    borderRadius: '12px',
    border: `1px solid ${colors.accent1}`,
    backgroundColor: 'rgba(126, 87, 194, 0.05)',
    padding: '16px',
    marginBottom: '20px',
    position: 'relative',
    overflow: 'hidden',
  },
  aiSuggestionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  aiSuggestionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: colors.accent1,
  },
  aiSuggestionContent: {
    fontSize: '14px',
    color: colors.textPrimary,
    lineHeight: '1.5',
  },
  aiSuggestionActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '12px',
  },
  aiSuggestionApply: {
    backgroundColor: colors.accent1,
    color: colors.iceWhite,
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: animations.transition,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '500',
    '&:hover': {
      opacity: 0.9,
    }
  },
  aiSuggestionDismiss: {
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    padding: '8px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: animations.transition,
    fontSize: '13px',
    '&:hover': {
      backgroundColor: colors.paleDawn,
    }
  },
  aiLoading: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '3px',
    backgroundColor: 'rgba(126, 87, 194, 0.2)',
    overflow: 'hidden',
  },
  aiLoadingBar: {
    position: 'absolute',
    top: 0,
    left: '0%',
    height: '100%',
    width: '30%',
    backgroundColor: colors.accent1,
    borderRadius: '4px',
    animation: 'aiLoading 1.5s infinite ease-in-out',
  },
  
  // Schedule Item Actions
  scheduleItemActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: `1px solid ${colors.paleDawn}`,
  },
  actionButtonGroup: {
    display: 'flex',
    gap: '8px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: animations.transition,
  },
  copyButton: {
    backgroundColor: 'rgba(66, 165, 245, 0.1)',
    color: colors.info,
    '&:hover': {
      backgroundColor: 'rgba(66, 165, 245, 0.2)',
    }
  },
  smartTimeButton: {
    backgroundColor: 'rgba(102, 187, 106, 0.1)',
    color: colors.accent3,
    '&:hover': {
      backgroundColor: 'rgba(102, 187, 106, 0.2)',
    }
  },
  
  // Quick time presets
  timePresets: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    overflowX: 'auto',
    padding: '4px 0',
  },
  timePreset: {
    padding: '6px 12px',
    borderRadius: '16px',
    border: `1px solid ${colors.paleDawn}`,
    backgroundColor: colors.iceWhite,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: animations.transition,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: colors.paleDawnLight,
      borderColor: colors.sunriseRed,
    }
  },
  timePresetActive: {
    backgroundColor: 'rgba(218, 41, 28, 0.1)',
    borderColor: colors.sunriseRed,
    color: colors.sunriseRed,
  },
  
  // PDF specific preview
  pdfPreview: {
    width: '100%',
    height: '300px',
    border: `1px solid ${colors.paleDawn}`,
    borderRadius: '8px',
    overflow: 'hidden',
    marginTop: '16px',
  },
  pdfPreviewHeader: {
    backgroundColor: colors.paleDawnLight,
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${colors.paleDawn}`,
  },
  pdfPreviewContent: {
    height: 'calc(100% - 40px)',
    overflow: 'auto',
    backgroundColor: colors.iceWhite,
  },
  pdfPage: {
    padding: '20px',
    margin: '10px auto',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    backgroundColor: colors.iceWhite,
    maxWidth: '600px',
  },
  pdfText: {
    fontSize: '12px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
  },
  
  // Responsive styles
  '@media (max-width: 768px)': {
    formContainer: {
      width: '100%',
      maxWidth: '100%',
      height: '100vh',
      maxHeight: '100vh',
      borderRadius: '0',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    },
    header: {
      padding: '16px',
      alignItems: 'center'
    },
    drawerHandle: {
      display: 'block'
    },
    formContent: {
      padding: '16px',
      maxHeight: 'calc(100vh - 180px)'
    },
    progressContainer: {
      padding: '16px 16px 40px'
    },
    progressBar: {
      left: '16px',
      right: '16px'
    },
    scheduleGrid: {
      gridTemplateColumns: '1fr',
      gap: '12px'
    },
    col6: {
      flexBasis: '100%',
      maxWidth: '100%'
    },
    buttonContainer: {
      padding: '12px 16px'
    },
    button: {
      padding: '10px 16px',
      fontSize: '13px'
    },
    timeGrid: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    }
  }
};

// InfoTooltip Component
const InfoTooltip = ({ title, description, details }) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: '50%', transform: 'translateX(-50%)' });

  // Handle tooltip position to ensure it stays in viewport
  useEffect(() => {
    if (isOpen && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      // Check if tooltip is out of bounds on the right
      if (rect.right > viewportWidth) {
        setTooltipPosition({ left: 'auto', right: 0, transform: 'none' });
      } 
      // Check if tooltip is out of bounds on the left
      else if (rect.left < 0) {
        setTooltipPosition({ left: 0, transform: 'none' });
      }
      // Reset to default centered position
      else {
        setTooltipPosition({ left: '50%', transform: 'translateX(-50%)' });
      }
    }
  }, [isOpen]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  return (
    <div 
      style={{
        position: 'relative',
        display: 'inline-block',
        marginLeft: '8px'
      }} 
      ref={tooltipRef}
      onClick={(e) => e.stopPropagation()}
    >
      <div 
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.paleDawn,
          color: colors.textSecondary,
          fontSize: '12px',
          cursor: 'pointer',
          transition: animations.transition
        }} 
        onClick={() => setIsOpen(!isOpen)}
      >
        i
      </div>
      
      <div style={{
        position: 'absolute',
        bottom: 'calc(100% + 15px)',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '280px',
        backgroundColor: colors.greyDusk,
        color: colors.iceWhite,
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.16)',
        zIndex: 100,
        opacity: isOpen ? 1 : 0,
        visibility: isOpen ? 'visible' : 'hidden',
        transition: 'opacity 0.2s ease, visibility 0.2s ease',
        pointerEvents: isOpen ? 'auto' : 'none',
        ...tooltipPosition
      }}>
        <h4 style={{
          fontSize: '14px',
          fontWeight: '600',
          marginBottom: '8px',
          color: colors.iceWhite
        }}>{title}</h4>
        <p style={{
          fontSize: '12px',
          lineHeight: '1.5',
          color: colors.paleDawn
        }}>{description}</p>
        
        {details && details.length > 0 && (
          <ul style={{
            margin: '8px 0 0',
            padding: '0 0 0 16px',
            fontSize: '12px',
            color: colors.paleDawn
          }}>
            {details.map((detail, index) => (
              <li key={index} style={{marginBottom: '4px'}}>{detail}</li>
            ))}
          </ul>
        )}
        
        <div style={{
          position: 'absolute',
          width: '0',
          height: '0',
          bottom: '-6px',
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: `6px solid ${colors.greyDusk}`
        }}></div>
      </div>
    </div>
  );
};

// Save Indicator component
const SaveIndicator = ({ visible, text = "Autosaved" }) => {
  return (
    <div style={{
      position: 'absolute',
      top: '14px',
      right: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 12px',
      borderRadius: '16px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: 'rgba(38, 166, 154, 0.1)',
      color: colors.success,
      transition: animations.transition,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(-10px)',
    }}>
      <span style={{ 
        display: 'inline-block', 
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: colors.success,
        animation: 'pulse 1.5s infinite'
      }}></span>
      {text}
    </div>
  );
};

// AI Suggestion Component
const AISuggestion = ({ suggestion, onApply, onDismiss, loading = false }) => {
  return (
    <div style={styles.aiSuggestionContainer}>
      {loading && (
        <div style={styles.aiLoading}>
          <div style={styles.aiLoadingBar}></div>
        </div>
      )}
      
      <div style={styles.aiSuggestionHeader}>
        <Zap size={18} color={colors.accent1} />
        <div style={styles.aiSuggestionTitle}>AI Suggestion</div>
      </div>
      
      <div style={styles.aiSuggestionContent}>
        {suggestion}
      </div>
      
      <div style={styles.aiSuggestionActions}>
        <button 
          style={styles.aiSuggestionDismiss}
          onClick={onDismiss}
        >
          Dismiss
        </button>
        <button 
          style={styles.aiSuggestionApply}
          onClick={onApply}
        >
          <Zap size={14} /> Apply
        </button>
      </div>
    </div>
  );
};

// Time Range Slider Component
const TimeRangeSlider = ({ startTime, endTime, onTimeChange }) => {
  const sliderRef = useRef(null);
  const startHandleRef = useRef(null);
  const endHandleRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [width, setWidth] = useState(0);
  
  // Convert time string to minutes (e.g., "09:30" -> 570)
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 480; // Default to 8:00 AM
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  // Convert minutes to time string (e.g., 570 -> "09:30")
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };
  
  // Calculate percentage for positioning
  const minutesToPercent = (minutes) => {
    // Assume day starts at 7:00 (420 mins) and ends at 23:00 (1380 mins)
    const dayStart = 420;
    const dayEnd = 1380;
    const dayLength = dayEnd - dayStart;
    
    return ((minutes - dayStart) / dayLength) * 100;
  };
  
  // Calculate position from percent
  const percentToMinutes = (percent) => {
    const dayStart = 420;
    const dayEnd = 1380;
    const dayLength = dayEnd - dayStart;
    
    return Math.round(dayStart + (percent / 100) * dayLength);
  };
  
  // Initialize slider dimensions
  useEffect(() => {
    if (sliderRef.current) {
      setWidth(sliderRef.current.offsetWidth);
      
      // Update on window resize
      const handleResize = () => {
        if (sliderRef.current) {
          setWidth(sliderRef.current.offsetWidth);
        }
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [sliderRef.current]);
  
  // Handle mouse/touch events for dragging
  useEffect(() => {
    const handleMove = (e) => {
      if (!dragging || !sliderRef.current) return;
      
      const rect = sliderRef.current.getBoundingClientRect();
      const clientX = e.clientX || (e.touches && e.touches[0].clientX);
      
      if (!clientX) return;
      
      // Calculate position as percentage
      let percent = ((clientX - rect.left) / rect.width) * 100;
      percent = Math.max(0, Math.min(100, percent));
      
      // Convert to minutes
      const minutes = percentToMinutes(percent);
      
      // Round to nearest 15 minutes (or whatever granularity you want)
      const roundedMinutes = Math.round(minutes / 15) * 15;
      
      // Update the appropriate time based on which handle is being dragged
      if (dragging === 'start') {
        // Don't let start time go past end time
        const endMinutes = timeToMinutes(endTime);
        if (roundedMinutes < endMinutes) {
          onTimeChange(minutesToTime(roundedMinutes), endTime);
        }
      } else if (dragging === 'end') {
        // Don't let end time go before start time
        const startMinutes = timeToMinutes(startTime);
        if (roundedMinutes > startMinutes) {
          onTimeChange(startTime, minutesToTime(roundedMinutes));
        }
      }
    };
    
    const handleUp = () => {
      setDragging(null);
    };
    
    if (dragging) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('mouseup', handleUp);
      document.addEventListener('touchend', handleUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchend', handleUp);
    };
  }, [dragging, startTime, endTime]);
  
  // Create hour markers for the slider
  const hourMarkers = useMemo(() => {
    return Array.from({ length: 17 }, (_, i) => i + 7); // 7 AM to 11 PM
  }, []);
  
  // Calculate positions
  const startPercent = minutesToPercent(timeToMinutes(startTime || '08:00'));
  const endPercent = minutesToPercent(timeToMinutes(endTime || '17:00'));
  
  return (
    <div style={styles.timeSliderContainer}>
      <div 
        ref={sliderRef}
        style={styles.timeSlider}
        onClick={(e) => {
          // Handle click directly on the slider (not the handles)
          if (e.target === sliderRef.current) {
            const rect = sliderRef.current.getBoundingClientRect();
            const percent = ((e.clientX - rect.left) / rect.width) * 100;
            const minutes = percentToMinutes(percent);
            const roundedMinutes = Math.round(minutes / 15) * 15;
            
            // Determine if we should move start or end handle based on which is closer
            const startDiff = Math.abs(timeToMinutes(startTime) - roundedMinutes);
            const endDiff = Math.abs(timeToMinutes(endTime) - roundedMinutes);
            
            if (startDiff < endDiff) {
              onTimeChange(minutesToTime(roundedMinutes), endTime);
            } else {
              onTimeChange(startTime, minutesToTime(roundedMinutes));
            }
          }
        }}
      >
        {/* Hour markers */}
        <div style={styles.timeSliderMarks}>
          {hourMarkers.map((hour) => (
            <div key={hour} style={{
              ...styles.timeSliderMark,
              left: `${minutesToPercent(hour * 60)}%`
            }}>
              <span style={styles.timeSliderMarkLabel}>
                {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
              </span>
            </div>
          ))}
        </div>
        
        {/* Selected range */}
        <div style={{
          ...styles.timeSliderRange,
          left: `${startPercent}%`,
          width: `${endPercent - startPercent}%`
        }}></div>
        
        {/* Start handle */}
        <div 
          ref={startHandleRef}
          style={{
            ...styles.timeSliderHandle,
            ...styles.timeSliderHandleStart,
            left: `${startPercent}%`
          }}
          onMouseDown={() => setDragging('start')}
          onTouchStart={() => setDragging('start')}
        >
          <span style={{
            transform: 'rotate(-90deg)',
            fontSize: '10px'
          }}>◀</span>
        </div>
        
        {/* End handle */}
        <div 
          ref={endHandleRef}
          style={{
            ...styles.timeSliderHandle,
            ...styles.timeSliderHandleEnd,
            left: `${endPercent}%`
          }}
          onMouseDown={() => setDragging('end')}
          onTouchStart={() => setDragging('end')}
        >
          <span style={{
            transform: 'rotate(90deg)',
            fontSize: '10px'
          }}>◀</span>
        </div>
        
        {/* Time display */}
        <div style={{
          ...styles.timeSliderTime,
          ...styles.timeSliderTimeStart,
          left: `${startPercent}%`
        }}>
          {startTime || '08:00'}
        </div>
        
        <div style={{
          ...styles.timeSliderTime,
          ...styles.timeSliderTimeEnd,
          left: `${endPercent}%`
        }}>
          {endTime || '17:00'}
        </div>
      </div>
    </div>
  );
};

// Time Selector Component
const TimeSelector = ({ value, onChange, label, required = false, error = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Common hour presets
  const hourPresets = [6,7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,23,24];
  
  // Common minute presets
  const minutePresets = [0, 15, 30, 45];
  
  // Format display time
  const displayTime = value 
    ? `${value.split(':')[0].padStart(2, '0')}:${value.split(':')[1].padStart(2, '0')}`
    : 'Select time';
    
  // Handle time selection and close the dropdown
  const handleTimeSelect = (hour, minute) => {
    const formattedHour = hour.toString().padStart(2, '0');
    const formattedMinute = minute.toString().padStart(2, '0');
    const newValue = `${formattedHour}:${formattedMinute}`;
    
    onChange(newValue);
    setIsOpen(false);
  };
  
  return (
    <div style={styles.timeSelector}>
      <label style={styles.label}>
        {label} {required && <span style={styles.required}>*</span>}
      </label>
      
      <div 
        style={{
          ...styles.input,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          borderColor: error ? colors.error : colors.paleDawn,
          boxShadow: error ? `0 0 0 1px ${colors.error}` : 'none'
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Clock size={16} color={colors.textSecondary} />
          <span>{displayTime}</span>
        </div>
        <ChevronDown 
          size={16} 
          color={colors.textSecondary} 
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease'
          }}
        />
      </div>
      
      {error && <div style={styles.errorText}>{error}</div>}
      
      {isOpen && (
        <div style={{
          ...styles.timeGrid,
          animation: 'fadeIn 0.2s ease'
        }}>
          {hourPresets.map(hour => (
            <div key={`hour-${hour}`}>
              {minutePresets.map(minute => {
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                const isActive = value === timeString;
                
                return (
                  <button
                    key={`time-${hour}-${minute}`}
                    type="button"
                    style={{
                      ...styles.timeButton,
                      ...(isActive ? styles.timeButtonActive : {})
                    }}
                    onClick={() => handleTimeSelect(hour, minute)}
                  >
                    {timeString}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Schedule Item Time Presets
const ScheduleTimePresets = ({ onSelect, activePreset, setActivePreset }) => {
  const presets = [
    { id: 'morning', name: 'Morning', start: '08:00', end: '12:00' },
    { id: 'lunch', name: 'Lunch', start: '12:00', end: '13:30' },
    { id: 'afternoon', name: 'Afternoon', start: '13:30', end: '17:00' },
    { id: 'evening', name: 'Evening', start: '18:00', end: '22:00' },
    { id: 'fullday', name: 'Full Day', start: '09:00', end: '17:00' },
    { id: 'breakfast', name: 'Breakfast', start: '07:30', end: '09:00' },
    { id: 'dinner', name: 'Dinner', start: '18:00', end: '19:30' },
  ];
  
  const handlePresetClick = (preset) => {
    setActivePreset(preset.id);
    onSelect(preset.start, preset.end);
  };
  
  return (
    <div style={styles.timePresets}>
      {presets.map(preset => (
        <button
          key={preset.id}
          type="button"
          style={{
            ...styles.timePreset,
            ...(activePreset === preset.id ? styles.timePresetActive : {})
          }}
          onClick={() => handlePresetClick(preset)}
        >
          {preset.name} ({preset.start}-{preset.end})
        </button>
      ))}
    </div>
  );
};

// Enhanced Group Selector Component with Member List
const GroupSelector = ({ 
  groups, 
  selectedGroups, 
  onChange, 
  isForAll, 
  onToggleForAll,
  groupMembers = {} // Map von groupId -> array of members
}) => {
  const [expandedGroup, setExpandedGroup] = useState(null);
  
  // Handler für die Gruppenauswahl mit Logging
  const handleGroupSelection = (groupId) => {
    // Aktuellen Zustand sichern
    const wasSelected = selectedGroups.includes(groupId);
    
    // Neue Auswahl berechnen
    const newSelection = wasSelected
      ? selectedGroups.filter(id => id !== groupId)  // Entfernen wenn ausgewählt
      : [...selectedGroups, groupId];                // Hinzufügen wenn nicht ausgewählt
    
    // Logging zur Fehlersuche
    console.log(`Gruppe ${groupId} ${wasSelected ? 'entfernt von' : 'hinzugefügt zu'} Auswahl. Neue Auswahl:`, newSelection);
    
    // Wenn keine Gruppen mehr ausgewählt sind, optional:
    if (newSelection.length === 0) {
      console.log("Keine Gruppen mehr ausgewählt. Erwäge, zu 'Für alle' zu wechseln.");
      // Hier könnten Sie automatisch zu "Für alle" wechseln: onToggleForAll(true);
    }
    
    // Parent's onChange mit der neuen Auswahl aufrufen
    onChange(newSelection);
  };
  
  return (
    <div style={styles.groupSelector}>
      <div style={styles.groupTitle}>
        <Users size={18} color={colors.textPrimary} />
        Assign to
      </div>
      
      <div style={styles.switchContainer}>
        <label style={{
          ...styles.switchSlider,
          ...(isForAll ? styles.switchActive : {})
        }}>
          <input
            type="checkbox"
            style={styles.switchInput}
            checked={isForAll}
            onChange={() => {
              console.log(`Umschalten 'Für alle' von ${isForAll} zu ${!isForAll}`);
              onToggleForAll(!isForAll);
            }}
          />
          <span style={{
            position: 'absolute',
            content: '""',
            height: '20px',
            width: '20px',
            left: '2px',
            bottom: '2px',
            backgroundColor: 'white',
            borderRadius: '50%',
            transition: 'transform 0.3s ease',
            transform: isForAll ? 'translateX(20px)' : 'translateX(0)'
          }}></span>
        </label>
        <span style={styles.switchLabel}>
          {isForAll ? 'All participants' : 'Specific groups'}
        </span>
      </div>
      
      {!isForAll && (
        <div style={styles.groupOptions}>
          {groups.map(group => {
            const isSelected = selectedGroups.includes(group.id);
            
            return (
              <div key={group.id}>
                <div 
                  style={{
                    ...styles.groupOption,
                    ...(isSelected ? styles.groupOptionActive : {})
                  }}
                  onClick={() => handleGroupSelection(group.id)}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: 1,
                  }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: group.colorCode || colors.accent1,
                    }}></span>
                    <span>{group.name}</span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span style={styles.groupCount}>
                      {groupMembers[group.id]?.length || group.memberCount || 0} members
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedGroup(expandedGroup === group.id ? null : group.id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {expandedGroup === group.id ? (
                        <ChevronUp size={18} color={colors.textSecondary} />
                      ) : (
                        <ChevronDown size={18} color={colors.textSecondary} />
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Member list for expanded group */}
                <div style={{
                  ...styles.groupMembersList,
                  ...(expandedGroup === group.id ? styles.groupMembersListExpanded : {})
                }}>
                  {groupMembers[group.id]?.length > 0 ? (
                    groupMembers[group.id].map((member, idx) => (
                      <div key={idx} style={styles.groupMember}>
                        <div style={{
                          ...styles.groupMemberAvatar,
                          backgroundColor: group.colorCode || colors.accent1,
                        }}>
                          {member.username?.[0]?.toUpperCase() || '?'}
                        </div>
                        <span>{member.username}</span>
                        {member.email && (
                          <span style={{ fontSize: '11px', color: colors.textSecondary, marginLeft: '4px' }}>
                            ({member.email})
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: '12px', color: colors.textSecondary, padding: '8px 0' }}>
                      No member information available
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Enhanced File Analyzer with PDF support
const FileAnalyzer = ({ onScheduleSuggestions }) => {
  const [file, setFile] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [pdfText, setPdfText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };
  
  const processFile = async (file) => {
    if (!file) return;
    
    setLoading(true);
    setError(null);
    setFile(file);
    
    try {
      // Handle different file types
      if (file.name.endsWith('.csv')) {
        processCsvFile(file);
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        processExcelFile(file);
      } else if (file.name.endsWith('.pdf')) {
        await processPdfFile(file);
      } else {
        throw new Error('Unsupported file type. Please upload CSV, Excel, or PDF files.');
      }
    } catch (err) {
      console.error("File processing error:", err);
      setError(err.message);
      setLoading(false);
    }
  };
  
  const processCsvFile = (file) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const csv = event.target.result;
      Papa.parse(csv, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setFileData(results.data);
          analyzeScheduleData(results.data);
          setLoading(false);
        },
        error: (error) => {
          setError(`Error parsing CSV: ${error.message}`);
          setLoading(false);
        }
      });
    };
    
    reader.onerror = () => {
      setError('Error reading file');
      setLoading(false);
    };
    
    reader.readAsText(file);
  };
  
  const processExcelFile = (file) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first worksheet
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        
        // Convert to JSON with headers
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        
        // Process data and extract headers
        if (jsonData.length > 0) {
          const headers = jsonData[0];
          const rows = jsonData.slice(1).map(row => {
            const obj = {};
            headers.forEach((header, i) => {
              obj[header] = row[i];
            });
            return obj;
          });
          
          setFileData(rows);
          analyzeScheduleData(rows);
        } else {
          setError('No data found in Excel file');
        }
        
        setLoading(false);
      } catch (err) {
        setError(`Error processing Excel file: ${err.message}`);
        setLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading file');
      setLoading(false);
    };
    
    reader.readAsArrayBuffer(file);
  };
  
  const processPdfFile = async (file) => {
    // In a real implementation, you would use pdf.js or a similar library
    // Here we'll use a simplified approach for demonstration
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        // In a real implementation with pdf.js:
        // const pdfData = new Uint8Array(event.target.result);
        // const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
        // 
        // let fullText = '';
        // for (let i = 1; i <= pdf.numPages; i++) {
        //   const page = await pdf.getPage(i);
        //   const content = await page.getTextContent();
        //   const pageText = content.items.map(item => item.str).join(' ');
        //   fullText += pageText + '\n';
        // }
        
        // For this example, we'll simulate PDF parsing with a simple text extraction
        const text = event.target.result;
        setPdfText(text);
        
        // Extract schedule items from the PDF text
        const items = extractScheduleItemsFromPdf(text);
        if (items.length > 0) {
          onScheduleSuggestions(items);
        }
        
        setLoading(false);
      } catch (err) {
        setError(`Error processing PDF file: ${err.message}`);
        setLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading file');
      setLoading(false);
    };
    
    // For binary PDFs, you'd use:
    // reader.readAsArrayBuffer(file);
    // For this demo, we'll just read as text:
    reader.readAsText(file);
  };
  
  // Extract schedule items from PDF text
  const extractScheduleItemsFromPdf = (text) => {
    // This is a simplified extraction that would be more sophisticated with real PDF parsing
    const items = [];
    
    // Look for patterns that might indicate schedule items
    // For the Sunrise camp example file specifically
    const dateRegex = /(\d{1,2}\.\d{1,2}\.\d{2,4})/g;
    const timeRangeRegex = /(\d{1,2}[:\.]\d{2})\s*-\s*(\d{1,2}[:\.]\d{2})/g;
    
    // Extract dates
    const dates = text.match(dateRegex) || [];
    
    // Extract time ranges
    const timeRanges = [];
    let match;
    while ((match = timeRangeRegex.exec(text)) !== null) {
      timeRanges.push({ start: match[1].replace('.', ':'), end: match[2].replace('.', ':') });
    }
    
    // Look for lines that might contain activities
    const lines = text.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines
      if (!line) continue;
      
      // Check if the line contains a time range
      const timeMatch = line.match(timeRangeRegex);
      if (timeMatch) {
        const startTime = timeMatch[0].split('-')[0].trim().replace('.', ':');
        const endTime = timeMatch[0].split('-')[1].trim().replace('.', ':');
        
        // Look for activity title - assume it's after the time range
        const title = line.substring(timeMatch.index + timeMatch[0].length).trim();
        
        // Add to items if we have enough info
        if (title && startTime) {
          items.push({
            title: title || 'Unnamed Activity',
            date: dates[0] ? formatDate(dates[0]) : new Date().toISOString().split('T')[0],
            startTime,
            endTime,
            location: '',
            description: `Extracted from PDF: ${line}`
          });
        }
      }
      // Special handling for the Sunrise camp file structure
      else if (line.includes('Zeiten') || line.includes('Gruppe')) {
        // This line might be a header, check the next lines for activities
        for (let j = i + 1; j < Math.min(i + 10, lines.length); j++) {
          const activityLine = lines[j].trim();
          
          // Skip empty lines
          if (!activityLine) continue;
          
          // Check for patterns in the Sunrise camp file
          if (activityLine.includes('fitness') || 
              activityLine.includes('Workshop') || 
              activityLine.includes('Mittag') ||
              activityLine.includes('Morgen') ||
              activityLine.includes('Abend')) {
            
            // Find associated time if available
            let activityTime = null;
            for (const timeRange of timeRanges) {
              if (j > 0 && lines[j-1].includes(timeRange.start)) {
                activityTime = timeRange;
                break;
              }
            }
            
            items.push({
              title: activityLine,
              date: dates[0] ? formatDate(dates[0]) : new Date().toISOString().split('T')[0],
              startTime: activityTime ? activityTime.start : '',
              endTime: activityTime ? activityTime.end : '',
              location: '',
              description: `Extracted from PDF`
            });
          }
        }
      }
    }
    
    // Handle the specific Sunrise camp table format
    if (text.includes('Sunrise Young Talent') && text.includes('Projektlager')) {
      // Special handling for the example file
      const specialItems = [
        {
          title: "Ankunft in Leysin",
          date: "2025-04-22",
          startTime: "12:30",
          endTime: "13:00",
          location: "Leysin",
          description: "Extracted from Sunrise Projektlager PDF"
        },
        {
          title: "Führung durchs Haus",
          date: "2025-04-22",
          startTime: "13:00",
          endTime: "14:00",
          location: "Main Building",
          description: "Extracted from Sunrise Projektlager PDF"
        },
        {
          title: "Welcome und Einführung",
          date: "2025-04-22",
          startTime: "14:00",
          endTime: "14:30",
          location: "Main Hall",
          description: "Extracted from Sunrise Projektlager PDF"
        },
        {
          title: "Fit fürs Leben - Der Start",
          date: "2025-04-22",
          startTime: "14:30",
          endTime: "15:30",
          location: "Workshop Room",
          description: "Extracted from Sunrise Projektlager PDF"
        },
        {
          title: "Morgenessen",
          date: "2025-04-23",
          startTime: "07:00",
          endTime: "08:30",
          location: "Dining Hall",
          description: "Extracted from Sunrise Projektlager PDF"
        }
      ];
      
      items.push(...specialItems);
    }
    
    return items;
  };
  
  // Format date string to YYYY-MM-DD
  const formatDate = (dateStr) => {
    // Parse common date formats like DD.MM.YYYY
    const parts = dateStr.split('.');
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
      return `${year}-${month}-${day}`;
    }
    
    // Default to today's date if parsing fails
    return new Date().toISOString().split('T')[0];
  };
  
  // AI analysis of schedule data
  const analyzeScheduleData = (data) => {
    if (!data || data.length === 0) {
      setError('No data found in file');
      return;
    }
    
    // Extract potential schedule items
    const suggestions = extractScheduleSuggestions(data);
    
    if (suggestions.length > 0) {
      onScheduleSuggestions(suggestions);
    } else {
      setError('No schedule items could be detected in this file');
    }
  };
  
  // Extract schedule suggestions from data
  const extractScheduleSuggestions = (data) => {
    const suggestions = [];
    const dateRegex = /(\d{1,2}[-./]\d{1,2}[-./]\d{2,4})|(\d{4}[-./]\d{1,2}[-./]\d{1,2})/;
    const timeRegex = /(\d{1,2}[:.]\d{2})\s*(?:-|to|–|until|\s)\s*(\d{1,2}[:.]\d{2})/i;
    
    // Look for potential schedule items in the data
    data.forEach(row => {
      const rowValues = Object.values(row).map(v => v?.toString() || '');
      const rowStr = rowValues.join(' ');
      
      // Find potential title (look for event-like words)
      const potentialTitle = rowValues.find(val => 
        val && val.length > 3 && 
        /meeting|event|activity|session|workshop|lunch|breakfast|dinner|presentation|morgen|mittag|abend|essen/i.test(val)
      );
      
      if (!potentialTitle) return;
      
      // Find date
      const dateMatch = rowStr.match(dateRegex);
      let dateStr = '';
      
      if (dateMatch) {
        dateStr = normalizeDate(dateMatch[0]);
      } else {
        // Default to today if no date found
        const today = new Date();
        dateStr = today.toISOString().split('T')[0];
      }
      
      // Find time
      const timeMatch = rowStr.match(timeRegex);
      
      // Only add if we have at least a title
      if (potentialTitle) {
        const suggestion = {
          title: potentialTitle,
          date: dateStr,
          startTime: timeMatch ? normalizeTime(timeMatch[1]) : '',
          endTime: timeMatch ? normalizeTime(timeMatch[2]) : '',
          location: extractLocation(rowValues),
          description: 'Imported from spreadsheet'
        };
        
        suggestions.push(suggestion);
      }
    });
    
    return suggestions;
  };
  
  // Helper functions for data extraction
  const normalizeDate = (dateStr) => {
    // Convert various date formats to YYYY-MM-DD
    const parts = dateStr.split(/[-./]/);
    
    // Determine the format based on the parts
    if (parts.length === 3) {
      let year, month, day;
      
      // Check if year is first (YYYY-MM-DD)
      if (parts[0].length === 4) {
        year = parts[0];
        month = parts[1].padStart(2, '0');
        day = parts[2].padStart(2, '0');
      } 
      // Assume DD-MM-YYYY or similar
      else {
        day = parts[0].padStart(2, '0');
        month = parts[1].padStart(2, '0');
        year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
      }
      
      return `${year}-${month}-${day}`;
    }
    
    // Return original if can't normalize
    return dateStr;
  };
  
  const normalizeTime = (timeStr) => {
    // Convert time to 24-hour format
    return timeStr.replace('.', ':');
  };
  
  const extractLocation = (values) => {
    const locationWords = ['room', 'hall', 'building', 'center', 'area', 'auditorium', 'classroom', 'raum', 'saal', 'gebäude'];
    return values.find(val => 
      val && locationWords.some(word => val.toLowerCase().includes(word))
    ) || '';
  };
  
  return (
    <>
      <div 
        style={{
          ...styles.fileUploadContainer,
          ...(isDragging ? styles.fileUploadActive : {})
        }}
        onClick={() => fileInputRef.current.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input 
          type="file" 
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".csv,.xlsx,.xls,.pdf"
          onChange={handleFileChange}
        />
        
        <div style={{ display: 'flex', gap: '16px' }}>
          <FileSpreadsheet size={32} color={colors.textSecondary} />
          <File  size={32} color={colors.textSecondary} />
        </div>
        
        <div style={styles.fileUploadText}>
          {loading ? 'Analyzing file...' : 'Drag & drop a spreadsheet or PDF or click to upload'}
          <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>
            Supported formats: CSV, Excel (.xlsx, .xls), PDF
          </div>
        </div>
      </div>
      
      {error && (
        <div style={{ color: colors.error, fontSize: '14px', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={16} color={colors.error} />
          {error}
        </div>
      )}
      
      {/* Show CSV/Excel preview */}
      {fileData && fileData.length > 0 && file && !file.name.endsWith('.pdf') && (
        <div style={styles.filePreview}>
          <div style={styles.filePreviewHeader}>
            <div style={{ fontWeight: '600', fontSize: '14px' }}>
              {file.name}
            </div>
            <div style={{ fontSize: '12px', color: colors.textSecondary }}>
              {fileData.length} rows
            </div>
          </div>
          
          <div style={styles.filePreviewContent}>
            <table style={styles.fileTable}>
              <thead style={styles.fileTableHead}>
                <tr>
                  {Object.keys(fileData[0]).slice(0, 5).map((header, i) => (
                    <th key={i} style={styles.fileTableCell}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fileData.slice(0, 5).map((row, i) => (
                  <tr key={i}>
                    {Object.values(row).slice(0, 5).map((cell, j) => (
                      <td key={j} style={styles.fileTableCell}>
                        {cell?.toString() || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            
            {fileData.length > 5 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '8px', 
                fontSize: '12px',
                color: colors.textSecondary
              }}>
                + {fileData.length - 5} more rows
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Show PDF preview */}
      {pdfText && file && file.name.endsWith('.pdf') && (
        <div style={styles.pdfPreview}>
          <div style={styles.pdfPreviewHeader}>
            <div style={{ fontWeight: '600', fontSize: '14px' }}>
              {file.name}
            </div>
            <div style={{ fontSize: '12px', color: colors.textSecondary }}>
              PDF Preview
            </div>
          </div>
          
          <div style={styles.pdfPreviewContent}>
            <div style={styles.pdfPage}>
              <pre style={styles.pdfText}>
                {pdfText.slice(0, 1000)}
                {pdfText.length > 1000 && '...'}
              </pre>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Enhanced Camp Management Form
const EnhancedCampManagementForm = ({ onClose, initialData = null }) => {
  // Phase descriptions
  const phaseDescriptions = {
    preCamp: {
      title: "Pre-Camp Phase",
      description: "This optional phase provides participants with preparatory information before the main camp begins.",
      details: [
        "Packing lists",
        "General information",
        "Login access"
      ]
    },
    mainCamp: {
      title: "Main Camp Phase",
      description: "The core phase of the camp where all features are available and participants have full access.",
      details: [
        "Complete app access",
        "All schedules",
        "All activities"
      ]
    },
    postCamp: {
      title: "Post-Camp Phase",
      description: "Optional phase after the main camp for follow-up activities and pending tasks.",
      details: [
        "Login access",
        "Pending tasks",
        "Task descriptions"
      ]
    }
  };
  
  // Enhanced default schedule entry with group targeting
  const defaultScheduleEntry = {
    id: null,
    title: '',
    date: '',
    startTime: '',
    endTime: '',
    endDate: '', // For multi-day events
    location: '',
    description: '',
    isMultiDay: false, // Flag for multi-day events
    forAllGroups: true, // Default to all groups
    targetGroupIds: [], // Specific groups if not for all
    isRepeating: false, // Flag for repeating events
    repeatFrequency: 'daily', // daily, weekly
    repeatUntil: '' // End date for repeating
  };

  // Default form data
  const defaultFormData = {
    id: null,
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    phases: {
      preCamp: {
        id: null,
        phaseType: 'PRE_CAMP',
        enabled: false,
        startDate: '',
        endDate: '',
        additionalInfo: '',
        packingItems: []
      },
      mainCamp: {
        id: null,
        phaseType: 'MAIN_CAMP',
        enabled: true,
        startDate: '',
        endDate: '',
        additionalInfo: ''
      },
      postCamp: {
        id: null,
        phaseType: 'POST_CAMP',
        enabled: false,
        startDate: '',
        endDate: '',
        additionalInfo: '',
        activityItems: []
      }
    },
    schedule: [defaultScheduleEntry]
  };

  // State management
  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [expandedScheduleItem, setExpandedScheduleItem] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [userGroups, setUserGroups] = useState([]);
  const [groupMembersCache, setGroupMembersCache] = useState({});
  const [scheduleSuggestions, setScheduleSuggestions] = useState([]);
  const [showAISuggestion, setShowAISuggestion] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [currentAiScheduleItem, setCurrentAiScheduleItem] = useState(null);
  const [activeTimePresets, setActiveTimePresets] = useState({});
  
  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  
  // Step completion state
  const [stepsCompleted, setStepsCompleted] = useState({
    1: false,
    2: false,
    3: false
  });
  
  // Autosave indicators
  const [saveIndicators, setSaveIndicators] = useState({
    basicInfo: false,
    preCamp: false,
    mainCamp: false,
    postCamp: false,
    schedule: false
  });
  
  // Schedule drawer modal for mobile view
  const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
  const [activeScheduleItem, setActiveScheduleItem] = useState(null);
  
  // Mobile drawer state
  const [drawerStartY, setDrawerStartY] = useState(null);
  const [drawerDeltaY, setDrawerDeltaY] = useState(0);
  const [isDrawerDragging, setIsDrawerDragging] = useState(false);
  
  const isMobile = windowWidth <= 768;

  // Animation references
  const formContentRef = useRef(null);
  const drawerRef = useRef(null);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      const transformedData = transformInitialData(initialData);
      setFormData(transformedData);
    }
    
    // Fetch user groups
    fetchUserGroups();
  }, [initialData]);

  // Scroll to top on step change
  useEffect(() => {
    if (formContentRef.current) {
      formContentRef.current.scrollTop = 0;
    }
  }, [currentStep]);
  
  // Fetch user groups and members
  const fetchUserGroups = async () => {
    try {
      const token = await localforage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${BASE_URL}/api/groups`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch groups');
      
      const data = await response.json();
      const groups = data.groups || [];
      setUserGroups(groups);
      
      // Fetch members for each group
      const membersCache = {};
      await Promise.all(groups.map(async (group) => {
        try {
          const groupResponse = await fetch(`${BASE_URL}/api/groups/${group.id}`, {
            headers: {
            'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (groupResponse.ok) {
            const groupData = await groupResponse.json();
            membersCache[group.id] = groupData.members || [];
          }
        } catch (error) {
          console.error(`Failed to fetch members for group ${group.id}:`, error);
        }
      }));
      
      setGroupMembersCache(membersCache);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  // Transform API data to form format
  const transformInitialData = (data) => {
    if (!data) return defaultFormData;
  
    // Ensure schedule is always an array
    let schedule = data.schedule?.map(item => {
      // Check if this is potentially a multi-day event
      const startDate = item.date || '';
      let endDate = '';
      let isMultiDay = false;
      
      if (item.endDate) {
        endDate = item.endDate;
        isMultiDay = true;
      }
      
      // Determine if the item is for specific groups
      // The backend property can be named either isForAllUsers or forAllUsers
      const forAllGroups = item.isForAllUsers !== undefined ? item.isForAllUsers : true;
      
      // Transform target groups from objects to IDs
      let targetGroupIds = [];
      if (item.targetGroups && Array.isArray(item.targetGroups)) {
        targetGroupIds = item.targetGroups.map(group => group.id);
      } else if (item.targetGroupIds && Array.isArray(item.targetGroupIds)) {
        targetGroupIds = [...item.targetGroupIds];
      }
      
      // Determine if the item is a repeating event
      const isRepeating = item.isRepeating || 
                         (item.recurrencePattern !== null && item.recurrencePattern !== undefined && 
                          item.recurrencePattern !== '');
      
      return {
        id: item.id || null,
        title: item.title || '',
        date: startDate,
        endDate: endDate,
        startTime: item.startTime || '',
        endTime: item.endTime || '',
        location: item.location || '',
        description: item.description || '',
        isMultiDay,
        forAllGroups,
        targetGroupIds,
        isRepeating,
        repeatFrequency: item.repeatFrequency || 'daily',
        repeatUntil: item.repeatUntil || ''
      };
    }) || [defaultScheduleEntry];
  
    // Find phases
    const preCampPhase = data.phases?.find(p => p.phaseType === 'PRE_CAMP');
    const mainCampPhase = data.phases?.find(p => p.phaseType === 'MAIN_CAMP');
    const postCampPhase = data.phases?.find(p => p.phaseType === 'POST_CAMP');
  
    return {
      id: data.id,
      title: data.title || '',
      description: data.description || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      phases: {
        preCamp: {
          id: preCampPhase?.id || null,
          phaseType: 'PRE_CAMP',
          enabled: preCampPhase?.enabled || false,
          startDate: preCampPhase?.startDate || '',
          endDate: preCampPhase?.endDate || '',
          additionalInfo: preCampPhase?.additionalInfo || '',
          packingItems: preCampPhase?.packingItems || []
        },
        mainCamp: {
          id: mainCampPhase?.id || null,
          phaseType: 'MAIN_CAMP',
          enabled: true,
          startDate: mainCampPhase?.startDate || '',
          endDate: mainCampPhase?.endDate || '',
          additionalInfo: mainCampPhase?.additionalInfo || ''
        },
        postCamp: {
          id: postCampPhase?.id || null,
          phaseType: 'POST_CAMP',
          enabled: postCampPhase?.enabled || false,
          startDate: postCampPhase?.startDate || '',
          endDate: postCampPhase?.endDate || '',
          additionalInfo: postCampPhase?.additionalInfo || '',
          activityItems: postCampPhase?.activityItems || []
        }
      },
      schedule: schedule
    };
  };
  
  // Aus der handleSubmit Funktion: Der prepareScheduleItems Teil
  const prepareScheduleItems = () => {
    return formData.schedule
      .filter(item => item.title?.trim() || item.date)
      .map(item => {
        const baseItem = {
          id: item.id || null,
          title: item.title?.trim() || '',
          date: item.date || '',
          startTime: item.startTime || '',
          endTime: item.endTime || '',
          location: item.location?.trim() || '',
          description: item.description?.trim() || '',
          // Use isForAllUsers to match what the backend expects
          isForAllUsers: item.forAllGroups,
          // Only include targetGroupIds if not for all users
          targetGroupIds: item.forAllGroups ? [] : item.targetGroupIds,
        };
        
        // Add endDate for multi-day events
        if (item.isMultiDay && item.endDate) {
          baseItem.endDate = item.endDate;
        }
        
        // Add recurrence properties for repeating events
        if (item.isRepeating) {
          baseItem.recurrencePattern = item.repeatFrequency;
          baseItem.repeatUntil = item.repeatUntil;
          // Also add the isRepeating flag itself for frontend use
          baseItem.isRepeating = true;
        }
        
        return baseItem;
      });
  };
  // Show save indicator function
  const showSaveIndicator = (section) => {
    setSaveIndicators(prev => ({
      ...prev,
      [section]: true
    }));
    
    // Hide indicator after 2 seconds
    setTimeout(() => {
      setSaveIndicators(prev => ({
        ...prev,
        [section]: false
      }));
    }, 2000);
  };

  // Form input handlers with autosave indicator
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [section, subsection, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section][subsection],
            [field]: type === 'checkbox' ? checked : value
          }
        }
      }));
      
      // Show appropriate save indicator
      if (section === 'phases') {
        showSaveIndicator(subsection);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
      
      // Show basic info save indicator
      showSaveIndicator('basicInfo');
    }
  };

  // Toggle handler for switches
  const handleToggle = (section, field) => {
    setFormData(prev => ({
      ...prev,
      phases: {
        ...prev.phases,
        [section]: {
          ...prev.phases[section],
          [field]: !prev.phases[section][field]
        }
      }
    }));
    
    // Show save indicator
    showSaveIndicator(section);
  };

  // Toggle multi-day event
  const handleMultiDayToggle = (index) => {
    setFormData(prev => ({
      ...prev,
      schedule: prev.schedule.map((item, i) => 
        i === index ? {
          ...item,
          isMultiDay: !item.isMultiDay,
          endDate: !item.isMultiDay ? item.date : item.endDate
        } : item
      )
    }));
    
    // Show schedule save indicator
    showSaveIndicator('schedule');
  };
  
  // Toggle repeating event
  const handleRepeatingToggle = (index) => {
    setFormData(prev => ({
      ...prev,
      schedule: prev.schedule.map((item, i) => 
        i === index ? {
          ...item,
          isRepeating: !item.isRepeating,
        } : item
      )
    }));
    
    // Show schedule save indicator
    showSaveIndicator('schedule');
  };
  // Toggle for all groups
  const handleForAllGroupsToggle = (index, value) => {
    console.log(`Umschalten "für alle" für Item #${index} auf:`, value);
    
    setFormData(prev => {
      // Deep copy des Schedule-Arrays erstellen
      const updatedSchedule = [...prev.schedule];
      
      // Existierendes Item finden und aktualisieren
      if (index >= 0 && index < updatedSchedule.length) {
        // Aktuelle targetGroupIds speichern (falls wir wieder zurückwechseln)
        const currentTargetGroups = [...(updatedSchedule[index].targetGroupIds || [])];
        
        // Deep copy des Items erstellen und forAllGroups aktualisieren
        updatedSchedule[index] = {
          ...updatedSchedule[index],
          forAllGroups: value,
          // targetGroupIds beibehalten für den Fall, dass wir später wieder umschalten
          targetGroupIds: currentTargetGroups
        };
      }
      
      // Aktualisiertes State zurückgeben
      return {
        ...prev,
        schedule: updatedSchedule
      };
    });
    
    // Änderung visuell bestätigen
    showSaveIndicator('schedule');
  };
  
  
  // Update target groups
  const handleTargetGroupsChange = (index, groupIds) => {
    setFormData(prev => {
      console.log(`Aktualisiere Zielgruppen für Item #${index}:`, groupIds);
      
      // Deep copy des Schedule-Arrays erstellen
      const updatedSchedule = [...prev.schedule];
      
      // Existierendes Item finden und aktualisieren
      if (index >= 0 && index < updatedSchedule.length) {
        // Deep copy des Items erstellen
        updatedSchedule[index] = {
          ...updatedSchedule[index],
          targetGroupIds: [...groupIds] // Neue Array-Instanz erstellen
        };
        
        // Wenn Gruppen ausgewählt wurden, forAllGroups auf false setzen
        if (groupIds.length > 0) {
          updatedSchedule[index].forAllGroups = false;
          console.log(`Item #${index} ist jetzt für spezifische Gruppen`);
        } else {
          // Optional: Wenn keine Gruppen ausgewählt wurden, aber trotzdem nicht für alle
          console.log(`Warnung: Item #${index} ist jetzt für spezifische Gruppen, hat aber keine Gruppen ausgewählt`);
        }
      }
      
      // Aktualisiertes State zurückgeben
      return {
        ...prev,
        schedule: updatedSchedule
      };
    });
    
    // Änderung visuell bestätigen
    showSaveIndicator('schedule');
  };
 
  // Packing items handlers
  const handlePackingItems = {
    add: () => {
      setFormData(prev => ({
        ...prev,
        phases: {
          ...prev.phases,
          preCamp: {
            ...prev.phases.preCamp,
            packingItems: [...prev.phases.preCamp.packingItems, { id: null, item: '' }]
          }
        }
      }));
      showSaveIndicator('preCamp');
    },
    remove: (index) => {
      setFormData(prev => ({
        ...prev,
        phases: {
          ...prev.phases,
          preCamp: {
            ...prev.phases.preCamp,
            packingItems: prev.phases.preCamp.packingItems.filter((_, i) => i !== index)
          }
        }
      }));
      showSaveIndicator('preCamp');
    },
    update: (index, value) => {
      setFormData(prev => ({
        ...prev,
        phases: {
          ...prev.phases,
          preCamp: {
            ...prev.phases.preCamp,
            packingItems: prev.phases.preCamp.packingItems.map((item, i) => 
              i === index ? { ...item, item: value } : item
            )
          }
        }
      }));
      showSaveIndicator('preCamp');
    }
  };

  // Activities handlers
  const handleActivities = {
    add: () => {
      setFormData(prev => ({
        ...prev,
        phases: {
          ...prev.phases,
          postCamp: {
            ...prev.phases.postCamp,
            activityItems: [...prev.phases.postCamp.activityItems, { id: null, activity: '' }]
          }
        }
      }));
      showSaveIndicator('postCamp');
    },
    remove: (index) => {
      setFormData(prev => ({
        ...prev,
        phases: {
          ...prev.phases,
          postCamp: {
            ...prev.phases.postCamp,
            activityItems: prev.phases.postCamp.activityItems.filter((_, i) => i !== index)
          }
        }
      }));
      showSaveIndicator('postCamp');
    },
    update: (index, value) => {
      setFormData(prev => ({
        ...prev,
        phases: {
          ...prev.phases,
          postCamp: {
            ...prev.phases.postCamp,
            activityItems: prev.phases.postCamp.activityItems.map((item, i) => 
              i === index ? { ...item, activity: value } : item
            )
          }
        }
      }));
      showSaveIndicator('postCamp');
    }
  };

  // Schedule handlers
  const handleScheduleChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: prev.schedule.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
    
    // Generate AI suggestions for schedule items
    if (field === 'title' && value.length > 3) {
      generateScheduleSuggestion(index, value, formData.schedule[index]);
    }
    
    // Show schedule save indicator
    showSaveIndicator('schedule');
  };
  
  // Update time range using the slider
  const handleTimeRangeChange = (index, startTime, endTime) => {
    setFormData(prev => ({
      ...prev,
      schedule: prev.schedule.map((item, i) => 
        i === index ? { 
          ...item, 
          startTime: startTime,
          endTime: endTime
        } : item
      )
    }));
    
    // Show schedule save indicator
    showSaveIndicator('schedule');
  };
  
  // Generate AI suggestions based on schedule title and context
  const generateScheduleSuggestion = (index, title, currentItem) => {
    // Don't suggest if we already have substantial data
    if (
      currentItem.location || 
      currentItem.description || 
      (currentItem.startTime && currentItem.endTime)
    ) {
      return;
    }
    
    // Set the current item for the AI suggestion
    setCurrentAiScheduleItem(index);
    
    // Look for patterns in title to suggest times, locations, etc.
    const lowerTitle = title.toLowerCase();
    
    // Start AI suggestion process
    setAiLoading(true);
    
    // Simulate AI thinking time
    setTimeout(() => {
      let suggestion = '';
      let hasMatch = false;
      
      // Breakfast suggestion
      if (lowerTitle.includes('breakfast') || lowerTitle.includes('morning meal') || lowerTitle.includes('morgenessen')) {
        suggestion = `This looks like a breakfast event. Would you like to set the time to 7:30-9:00 AM and location to "Dining Hall"?`;
        hasMatch = true;
      }
      // Lunch suggestion
      else if (lowerTitle.includes('lunch') || lowerTitle.includes('midday') || lowerTitle.includes('mittag')) {
        suggestion = `This appears to be a lunch event. Would you like to set the time to 12:00-13:30 and location to "Dining Hall"?`;
        hasMatch = true;
      }
      // Dinner suggestion
      else if (lowerTitle.includes('dinner') || lowerTitle.includes('evening meal') || lowerTitle.includes('abendessen')) {
        suggestion = `This seems to be a dinner event. Would you like to set the time to 18:00-19:30 and location to "Dining Hall"?`;
        hasMatch = true;
      }
      // Workshop suggestion
      else if (lowerTitle.includes('workshop') || lowerTitle.includes('session')) {
        suggestion = `For this workshop, would you like to set a 90-minute session (e.g., 14:00-15:30) in the "Workshop Room"?`;
        hasMatch = true;
      }
      // Meeting suggestion
      else if (lowerTitle.includes('meeting') || lowerTitle.includes('discussion')) {
        suggestion = `This appears to be a meeting. Would you like to schedule it for 60 minutes (e.g., 10:00-11:00) in the "Conference Room"?`;
        hasMatch = true;
      }
      // Activity suggestion
      else if (lowerTitle.includes('activity') || lowerTitle.includes('game')) {
        suggestion = `For this activity, would you like to set it for 13:30-15:00 in the "Activity Area" or "Sports Field"?`;
        hasMatch = true;
      }
      // Fitness suggestion
      else if (lowerTitle.includes('fitness') || lowerTitle.includes('sport') || lowerTitle.includes('workout')) {
        suggestion = `This seems to be a fitness activity. Would you like to schedule it for 16:00-17:30 in the "Sports Hall"?`;
        hasMatch = true;
      }
      
      // Only show if we have a relevant suggestion
      if (hasMatch) {
        setAiSuggestion(suggestion);
        setShowAISuggestion(true);
      }
      
      setAiLoading(false);
    }, 1000);
  };
  
  // Handle AI suggestion application
  const applyAiSuggestion = () => {
    if (currentAiScheduleItem === null) return;
    
    const lowerTitle = formData.schedule[currentAiScheduleItem]?.title.toLowerCase() || '';
    
    // Apply different defaults based on the title
    if (lowerTitle.includes('breakfast') || lowerTitle.includes('morgenessen')) {
      handleScheduleChange(currentAiScheduleItem, 'startTime', '07:30');
      handleScheduleChange(currentAiScheduleItem, 'endTime', '09:00');
      handleScheduleChange(currentAiScheduleItem, 'location', 'Dining Hall');
      setActiveTimePresets({...activeTimePresets, [currentAiScheduleItem]: 'breakfast'});
    }
    else if (lowerTitle.includes('lunch') || lowerTitle.includes('mittag')) {
      handleScheduleChange(currentAiScheduleItem, 'startTime', '12:00');
      handleScheduleChange(currentAiScheduleItem, 'endTime', '13:30');
      handleScheduleChange(currentAiScheduleItem, 'location', 'Dining Hall');
      setActiveTimePresets({...activeTimePresets, [currentAiScheduleItem]: 'lunch'});
    }
    else if (lowerTitle.includes('dinner') || lowerTitle.includes('abendessen')) {
      handleScheduleChange(currentAiScheduleItem, 'startTime', '18:00');
      handleScheduleChange(currentAiScheduleItem, 'endTime', '19:30');
      handleScheduleChange(currentAiScheduleItem, 'location', 'Dining Hall');
      setActiveTimePresets({...activeTimePresets, [currentAiScheduleItem]: 'dinner'});
    }
    else if (lowerTitle.includes('workshop') || lowerTitle.includes('session')) {
      handleScheduleChange(currentAiScheduleItem, 'startTime', '14:00');
      handleScheduleChange(currentAiScheduleItem, 'endTime', '15:30');
      handleScheduleChange(currentAiScheduleItem, 'location', 'Workshop Room');
      setActiveTimePresets({...activeTimePresets, [currentAiScheduleItem]: 'afternoon'});
    }
    else if (lowerTitle.includes('meeting') || lowerTitle.includes('discussion')) {
      handleScheduleChange(currentAiScheduleItem, 'startTime', '10:00');
      handleScheduleChange(currentAiScheduleItem, 'endTime', '11:00');
      handleScheduleChange(currentAiScheduleItem, 'location', 'Conference Room');
      setActiveTimePresets({...activeTimePresets, [currentAiScheduleItem]: 'morning'});
    }
    else if (lowerTitle.includes('activity') || lowerTitle.includes('game')) {
      handleScheduleChange(currentAiScheduleItem, 'startTime', '13:30');
      handleScheduleChange(currentAiScheduleItem, 'endTime', '15:00');
      handleScheduleChange(currentAiScheduleItem, 'location', 'Activity Area');
      setActiveTimePresets({...activeTimePresets, [currentAiScheduleItem]: 'afternoon'});
    }
    else if (lowerTitle.includes('fitness') || lowerTitle.includes('sport')) {
      handleScheduleChange(currentAiScheduleItem, 'startTime', '16:00');
      handleScheduleChange(currentAiScheduleItem, 'endTime', '17:30');
      handleScheduleChange(currentAiScheduleItem, 'location', 'Sports Hall');
      setActiveTimePresets({...activeTimePresets, [currentAiScheduleItem]: 'afternoon'});
    }
    
    // Always hide the suggestion after applying
    setShowAISuggestion(false);
  };
  
  // Handle schedule suggestions from file upload
  const handleScheduleSuggestions = (suggestions) => {
    if (!suggestions || suggestions.length === 0) return;
    
    // Add suggested schedule items
    setFormData(prev => ({
      ...prev,
      schedule: [
        ...prev.schedule,
        ...suggestions.map(suggestion => ({
          ...defaultScheduleEntry,
          ...suggestion
        }))
      ]
    }));
    
    // Show success message
    setAiSuggestion(`Successfully imported ${suggestions.length} schedule items.`);
    setShowAISuggestion(true);
    
    // Show schedule save indicator
    showSaveIndicator('schedule');
  };

  const addScheduleItem = () => {
    // Create a brand new schedule item with a unique reference
    const newScheduleItem = { ...defaultScheduleEntry };
    
    // Update form data with proper immutable state handling
    setFormData(prev => ({
      ...prev,
      schedule: [...prev.schedule, newScheduleItem]
    }));
    
    // Set timeout to ensure the state has updated before trying to expand the new item
    setTimeout(() => {
      // Ensure we're expanding the correct index (last item in the updated array)
      setExpandedScheduleItem(formData.schedule.length);
      
      // If on mobile, open the drawer for the new item
      if (isMobile) {
        setActiveScheduleItem(formData.schedule.length);
        setScheduleDrawerOpen(true);
      }
    }, 50);
    
    // Show schedule save indicator
    showSaveIndicator('schedule');
  };
  
  // Fixed copyScheduleItem function
  const copyScheduleItem = (index) => {
    // Create a deep copy to avoid reference issues
    const itemToCopy = JSON.parse(JSON.stringify(formData.schedule[index]));
    
    // Remove ID to create a new item
    itemToCopy.id = null;
    itemToCopy.title = `${itemToCopy.title} (Copy)`;
    
    setFormData(prev => ({
      ...prev,
      schedule: [...prev.schedule, itemToCopy]
    }));
    
    // Show schedule save indicator
    showSaveIndicator('schedule');
    
    // Open the copied item
    setTimeout(() => {
      setExpandedScheduleItem(formData.schedule.length);
    }, 50);
  };
  
  // Fixed removeScheduleItem function
  const removeScheduleItem = (index) => {
    if (formData.schedule.length === 1) {
      // If this is the last item, reset it instead of removing
      setFormData(prev => ({
        ...prev,
        schedule: [{ ...defaultScheduleEntry }]
      }));
    } else {
      // Create a new array without the item at specified index
      setFormData(prev => {
        const updatedSchedule = [...prev.schedule];
        updatedSchedule.splice(index, 1);
        
        return {
          ...prev,
          schedule: updatedSchedule
        };
      });
      
      // Reset expanded item if removed
      if (expandedScheduleItem === index) {
        setExpandedScheduleItem(null);
      } else if (expandedScheduleItem > index) {
        // Adjust index if an item before the expanded one was removed
        setExpandedScheduleItem(expandedScheduleItem - 1);
      }
    }
    
    // Show schedule save indicator
    showSaveIndicator('schedule');
  };
  
  // Apply time preset
  const handleTimePreset = (index, startTime, endTime, presetId) => {
    handleScheduleChange(index, 'startTime', startTime);
    handleScheduleChange(index, 'endTime', endTime);
    
    // Update active preset
    setActiveTimePresets({...activeTimePresets, [index]: presetId});
  };

  // Section update handlers
  const handleUpdateSection = (step) => {
    // Validate just this section
    if (validateStep(step)) {
      // Mark step as completed
      setStepsCompleted(prev => ({
        ...prev,
        [step]: true
      }));
      
      // Show appropriate save indicator
      if (step === 1) showSaveIndicator('basicInfo');
      if (step === 2) {
        showSaveIndicator('mainCamp');
        if (formData.phases.preCamp.enabled) showSaveIndicator('preCamp');
        if (formData.phases.postCamp.enabled) showSaveIndicator('postCamp');
      }
      if (step === 3) showSaveIndicator('schedule');
    }
  };

  // Step navigation
  const goToNextStep = () => {
    // Validate current step first
    if (validateStep(currentStep)) {
      // Mark current step as completed
      setStepsCompleted(prev => ({
        ...prev,
        [currentStep]: true
      }));
      
      // Apply transition animation to form content
      if (formContentRef.current) {
        formContentRef.current.style.opacity = 0;
        formContentRef.current.style.transform = 'translateX(20px)';
        
        setTimeout(() => {
          // Move to next step
          setCurrentStep(prev => Math.min(prev + 1, totalSteps));
          
          // Reset animation
          setTimeout(() => {
            if (formContentRef.current) {
              formContentRef.current.style.opacity = 1;
              formContentRef.current.style.transform = 'translateX(0)';
            }
          }, 50);
        }, 300);
      } else {
        // Fallback if ref is not available
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      }
    }
  };

  const goToPrevStep = () => {
    // Apply transition animation to form content
    if (formContentRef.current) {
      formContentRef.current.style.opacity = 0;
      formContentRef.current.style.transform = 'translateX(-20px)';
      
      setTimeout(() => {
        // Move to previous step
        setCurrentStep(prev => Math.max(prev - 1, 1));
        
        // Reset animation
        setTimeout(() => {
          if (formContentRef.current) {
            formContentRef.current.style.opacity = 1;
            formContentRef.current.style.transform = 'translateX(0)';
          }
        }, 50);
      }, 300);
    } else {
      // Fallback if ref is not available
      setCurrentStep(prev => Math.max(prev - 1, 1));
    }
  };

  // Step-specific validation
  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      // Basic information validation
      if (!formData.title?.trim()) {
        newErrors.title = 'Title is required';
      }
      if (!formData.startDate) {
        newErrors.startDate = 'Start date is required';
      }
      if (!formData.endDate) {
        newErrors.endDate = 'End date is required';
      }
      
      // Date range validation
      if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        if (start > end) {
          newErrors.endDate = 'End date must be after start date';
        }
      }
    }
    
    else if (step === 2) {
      // Phases validation
      
      // Main camp validation (always required)
      if (!formData.phases.mainCamp.startDate) {
        newErrors['phases.mainCamp.startDate'] = 'Main camp start date is required';
      }
      if (!formData.phases.mainCamp.endDate) {
        newErrors['phases.mainCamp.endDate'] = 'Main camp end date is required';
      }
      
      // Pre-camp validation (only if enabled)
      if (formData.phases.preCamp.enabled) {
        if (!formData.phases.preCamp.startDate) {
          newErrors['phases.preCamp.startDate'] = 'Pre-camp start date is required';
        }
        if (!formData.phases.preCamp.endDate) {
          newErrors['phases.preCamp.endDate'] = 'Pre-camp end date is required';
        }
        
        // Date range validation
        if (formData.phases.preCamp.startDate && formData.phases.preCamp.endDate) {
          const start = new Date(formData.phases.preCamp.startDate);
          const end = new Date(formData.phases.preCamp.endDate);
          if (start > end) {
            newErrors['phases.preCamp.endDate'] = 'End date must be after start date';
          }
          
          // Check that pre-camp ends before main camp starts
          if (formData.phases.mainCamp.startDate) {
            const mainStart = new Date(formData.phases.mainCamp.startDate);
            if (end > mainStart) {
              newErrors['phases.preCamp.endDate'] = 'Pre-camp must end before main camp starts';
            }
          }
        }
      }
      
      // Main camp date range validation
      if (formData.phases.mainCamp.startDate && formData.phases.mainCamp.endDate) {
        const start = new Date(formData.phases.mainCamp.startDate);
        const end = new Date(formData.phases.mainCamp.endDate);
        if (start > end) {
          newErrors['phases.mainCamp.endDate'] = 'End date must be after start date';
        }
      }
      
      // Post-camp validation (only if enabled)
      if (formData.phases.postCamp.enabled) {
        if (!formData.phases.postCamp.startDate) {
          newErrors['phases.postCamp.startDate'] = 'Post-camp start date is required';
        }
        if (!formData.phases.postCamp.endDate) {
          newErrors['phases.postCamp.endDate'] = 'Post-camp end date is required';
        }
        
        // Date range validation
        if (formData.phases.postCamp.startDate && formData.phases.postCamp.endDate) {
          const start = new Date(formData.phases.postCamp.startDate);
          const end = new Date(formData.phases.postCamp.endDate);
          if (start > end) {
            newErrors['phases.postCamp.endDate'] = 'End date must be after start date';
          }
          
          // Check that post-camp starts after main camp ends
          if (formData.phases.mainCamp.endDate) {
            const mainEnd = new Date(formData.phases.mainCamp.endDate);
            if (start < mainEnd) {
              newErrors['phases.postCamp.startDate'] = 'Post-camp must start after main camp ends';
            }
          }
        }
      }
    }
    
    else if (step === 3) {
      // Schedule validation
      formData.schedule.forEach((item, index) => {
        if (!item.title?.trim()) {
          newErrors[`schedule.${index}.title`] = 'Title is required';
        }
        if (!item.date) {
          newErrors[`schedule.${index}.date`] = 'Date is required';
        }
        
        // Multi-day event validation
        if (item.isMultiDay && !item.endDate) {
          newErrors[`schedule.${index}.endDate`] = 'End date is required for multi-day events';
        }
        
        // Check that end date is after start date for multi-day events
        if (item.isMultiDay && item.date && item.endDate) {
          const start = new Date(item.date);
          const end = new Date(item.endDate);
          if (start > end) {
            newErrors[`schedule.${index}.endDate`] = 'End date must be after start date';
          }
        }
        
        // Repeating event validation
        if (item.isRepeating && !item.repeatUntil) {
          newErrors[`schedule.${index}.repeatUntil`] = 'Repeat until date is required';
        }
        
        // Target groups validation
        if (!item.forAllGroups && (!item.targetGroupIds || item.targetGroupIds.length === 0)) {
          newErrors[`schedule.${index}.targetGroupIds`] = 'Please select at least one group';
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Complete form validation
  const validateForm = () => {
    // Validate all steps
    const step1Valid = validateStep(1);
    const currentStepBackup = currentStep;
    setCurrentStep(1); // Temporarily switch to step 1 for validation
    
    if (!step1Valid) {
      return false;
    }
    
    const step2Valid = validateStep(2);
    setCurrentStep(2); // Temporarily switch to step 2 for validation
    
    if (!step2Valid) {
      return false;
    }
    
    const step3Valid = validateStep(3);
    setCurrentStep(currentStepBackup); // Switch back to original step
    
    return step3Valid;
  };

  // Form submission
  // Verbesserte Submit-Funktion mit korrekter Gruppenverwaltung
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
  
    setLoading(true);
    setFormError(null);
  
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('Nicht eingeloggt');
  
      // Säuberung und Validierung der Packlisten-Einträge
      const cleanPackingItems = (items = []) => {
        return items
          .filter(item => item && typeof item === 'object' && item.item?.trim())
          .map(item => ({
            id: item.id || null,
            item: item.item.trim()
          }));
      };
  
      // Säuberung und Validierung der Aktivitäten
      const cleanActivityItems = (items = []) => {
        return items
          .filter(item => item && typeof item === 'object' && item.activity?.trim())
          .map(item => ({
            id: item.id || null,
            activity: item.activity.trim()
          }));
      };
  
      // Vorbereitung der Phasen mit richtiger Struktur
      const preparePhase = (phase, type) => {
        const basePhase = {
          id: phase.id || null,
          phaseType: type,
          enabled: type === 'MAIN_CAMP' ? true : !!phase.enabled,
          startDate: phase.startDate || formData.startDate,
          endDate: phase.endDate || formData.endDate,
          additionalInfo: phase.additionalInfo || ''
        };
  
        // Spezifische Felder je nach Phasentyp hinzufügen
        if (type === 'PRE_CAMP' && phase.enabled) {
          return {
            ...basePhase,
            packingItems: cleanPackingItems(phase.packingItems)
          };
        }
  
        if (type === 'POST_CAMP' && phase.enabled) {
          return {
            ...basePhase,
            activityItems: cleanActivityItems(phase.activityItems)
          };
        }
  
        // Für MAIN_CAMP oder deaktivierte Phasen, nur die Basis-Phase zurückgeben
        return basePhase;
      };
  
      // Verbesserte Funktion zur Vorbereitung der Schedule-Items
      const prepareScheduleItems = () => {
        console.log("Schedule-Items vorbereiten:", formData.schedule.length);
        
        // Doppelte Einträge verhindern durch Verwendung einer Map mit eindeutigen Schlüsseln
        const uniqueItems = new Map();
        
        // Durch alle Schedule-Items iterieren
        formData.schedule
          .filter(item => item.title?.trim() || item.date)
          .forEach(item => {
            // Einen eindeutigen Key für jedes Item erstellen
            const itemKey = `${item.title}-${item.date}-${item.startTime}`;
            
            // Nur einzigartige Items in die Map einfügen
            if (!uniqueItems.has(itemKey)) {
              // Jedes Item als neues Objekt erstellen
              const baseItem = {
                id: item.id || null,
                title: item.title?.trim() || '',
                date: item.date || '',
                startTime: item.startTime || '',
                endTime: item.endTime || '',
                location: item.location?.trim() || '',
                description: item.description?.trim() || '',
                // WICHTIG: Das Backend erwartet isForAllUsers, während Frontend forAllGroups verwendet
                isForAllUsers: item.forAllGroups
              };
              
              console.log(`Item vorbereiten: ${baseItem.title}, isForAllUsers: ${baseItem.isForAllUsers}`);
              
              // TargetGroupIds nur setzen, wenn nicht für alle UND vorhanden
              if (!item.forAllGroups && item.targetGroupIds && item.targetGroupIds.length > 0) {
                // Neue Array-Instanz erstellen
                baseItem.targetGroupIds = [...item.targetGroupIds];
                console.log(`  mit Zielgruppen: ${baseItem.targetGroupIds}`);
              } else {
                // Leeres Array, wenn keine Zielgruppen oder für alle Benutzer
                baseItem.targetGroupIds = [];
              }
              
              // EndDate für mehrtägige Veranstaltungen hinzufügen
              if (item.isMultiDay && item.endDate) {
                baseItem.endDate = item.endDate;
              }
              
              // Eigenschaften für wiederkehrende Veranstaltungen hinzufügen
              if (item.isRepeating) {
                baseItem.recurrencePattern = item.repeatFrequency;
                baseItem.repeatUntil = item.repeatUntil;
                baseItem.isRepeating = true;
              }
              
              // Item in die Map einfügen
              uniqueItems.set(itemKey, baseItem);
            }
          });
        
        // Map zu Array konvertieren
        return Array.from(uniqueItems.values());
      };
  
      // Zusammenstellung der Daten für die Übermittlung
      const submissionData = {
        id: formData.id || null,
        title: formData.title.trim(),
        description: formData.description?.trim() || '',
        startDate: formData.startDate,
        endDate: formData.endDate,
        phases: [
          preparePhase(formData.phases.preCamp, 'PRE_CAMP'),
          preparePhase(formData.phases.mainCamp, 'MAIN_CAMP'),
          preparePhase(formData.phases.postCamp, 'POST_CAMP')
        ],
        schedule: prepareScheduleItems()
      };
  
      console.log('Camp-Daten übermitteln:', JSON.stringify(submissionData, null, 2));
  
      const response = await fetch(
        `${BASE_URL}/api/camps${formData.id ? `/${formData.id}` : ''}`,
        {
          method: formData.id ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(submissionData)
        }
      );
  
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server-Antwort bei Fehler:', errorData);
        throw new Error(errorData.message || `Serverfehler: ${response.status}`);
      }
  
      await response.json();
      onClose(true);
  
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setFormError(`Fehler beim Speichern: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Utility functions for display formatting
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return new Date(`1970-01-01T${timeStr}`).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mobile drawer touch handlers
  const handleDrawerTouchStart = (e) => {
    const touch = e.touches[0];
    setDrawerStartY(touch.clientY);
    setIsDrawerDragging(true);
  };

  const handleDrawerTouchMove = (e) => {
    if (!isDrawerDragging || !drawerStartY) return;
    
    const touch = e.touches[0];
    const deltaY = touch.clientY - drawerStartY;
    
    if (deltaY > 0) { // Only allow downward movement
      setDrawerDeltaY(deltaY);
      if (drawerRef.current) {
        drawerRef.current.style.transform = `translateY(${deltaY}px)`;
      }
    }
  };

  const handleDrawerTouchEnd = () => {
    setIsDrawerDragging(false);
    
    if (drawerDeltaY > 100) {
      // If dragged down significantly, close the drawer
      setScheduleDrawerOpen(false);
    } else {
      // Otherwise, snap back
      if (drawerRef.current) {
        drawerRef.current.style.transform = 'translateY(0)';
      }
    }
    
    setDrawerDeltaY(0);
    setDrawerStartY(null);
  };

  // Step content rendering
  const renderBasicInfoStep = () => (
    <div style={styles.section}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: colors.sunriseRed,
          color: colors.iceWhite,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '12px',
          fontWeight: 'bold'
        }}>1</div>
        <div style={{
          fontSize: '18px',
          fontWeight: '600',
          color: colors.textPrimary
        }}>Basic Information</div>
      </div>
      
      <div style={styles.card}>
        <SaveIndicator visible={saveIndicators.basicInfo} />
        
        <div style={styles.formGroup}>
          <label style={styles.label}>
            Title <span style={styles.required}>*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            style={{
              ...styles.input,
              borderColor: errors.title ? colors.error : colors.paleDawn,
              boxShadow: errors.title ? '0 0 0 1px ' + colors.error : 'none'
            }}
            placeholder="Enter camp title"
          />
          {errors.title && (
            <div style={styles.errorText}>
              <AlertCircle size={14} color={colors.error} />
              {errors.title}
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            style={styles.textarea}
            placeholder="Enter camp description"
          />
        </div>

        <div style={styles.row}>
          <div style={styles.col6}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Start Date <span style={styles.required}>*</span>
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                style={{
                  ...styles.input,
                  borderColor: errors.startDate ? colors.error : colors.paleDawn,
                  boxShadow: errors.startDate ? '0 0 0 1px ' + colors.error : 'none'
                }}
              />
              {errors.startDate && (
                <div style={styles.errorText}>
                  <AlertCircle size={14} color={colors.error} />
                  {errors.startDate}
                </div>
              )}
            </div>
          </div>
          <div style={styles.col6}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                End Date <span style={styles.required}>*</span>
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                style={{
                  ...styles.input,
                  borderColor: errors.endDate ? colors.error : colors.paleDawn,
                  boxShadow: errors.endDate ? '0 0 0 1px ' + colors.error : 'none'
                }}
              />
              {errors.endDate && (
                <div style={styles.errorText}>
                  <AlertCircle size={14} color={colors.error} />
                  {errors.endDate}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <button
          type="button"
          onClick={() => handleUpdateSection(1)}
          style={{
            marginTop: '20px', 
            width: '100%', 
            padding: '12px',
            backgroundColor: colors.paleDawnLight,
            color: colors.textPrimary,
            fontWeight: '600',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: animations.transition,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            '&:hover': {
              backgroundColor: colors.paleDawn
            }
          }}
        >
          <Save size={16} /> Update Basic Information
        </button>
      </div>
    </div>
  );

  const renderPhasesStep = () => (
    <div style={styles.section}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: colors.sunriseRed,
          color: colors.iceWhite,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '12px',
          fontWeight: 'bold'
        }}>2</div>
        <div style={{
          fontSize: '18px',
          fontWeight: '600',
          color: colors.textPrimary
        }}>Camp Phases</div>
      </div>
      
      {/* Pre-Camp Phase */}
      <div style={styles.card}>
        <SaveIndicator visible={saveIndicators.preCamp} />
        
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Pre-Camp Phase
            </span>
          </h3>
          <InfoTooltip 
            title={phaseDescriptions.preCamp.title}
            description={phaseDescriptions.preCamp.description}
            details={phaseDescriptions.preCamp.details}
          />
        </div>
        
        <div style={styles.switchContainer}>
          <label style={{
            ...styles.switchSlider,
            ...(formData.phases.preCamp.enabled ? styles.switchActive : {})
          }}>
            <input
              type="checkbox"
              style={styles.switchInput}
              checked={formData.phases.preCamp.enabled}
              onChange={() => handleToggle('preCamp', 'enabled')}
            />
            <span style={{
              position: 'absolute',
              content: '""',
              height: '20px',
              width: '20px',
              left: '2px',
              bottom: '2px',
              backgroundColor: 'white',
              borderRadius: '50%',
              transition: 'transform 0.3s ease',
              transform: formData.phases.preCamp.enabled ? 'translateX(20px)' : 'translateX(0)'
            }}></span>
          </label>
          <span style={styles.switchLabel}>
            {formData.phases.preCamp.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {formData.phases.preCamp.enabled && (
          <div style={{ 
            marginTop: '24px', 
            animation: 'fadeIn 0.3s ease forwards',
            overflow: 'hidden' 
          }}>
            <div style={styles.row}>
              <div style={styles.col6}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Start Date <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="date"
                    name="phases.preCamp.startDate"
                    value={formData.phases.preCamp.startDate}
                    onChange={handleInputChange}
                    style={{
                      ...styles.input,
                      borderColor: errors['phases.preCamp.startDate'] ? colors.error : colors.paleDawn,
                      boxShadow: errors['phases.preCamp.startDate'] ? '0 0 0 1px ' + colors.error : 'none'
                    }}
                  />
                  {errors['phases.preCamp.startDate'] && (
                    <div style={styles.errorText}>
                      <AlertCircle size={14} color={colors.error} />
                      {errors['phases.preCamp.startDate']}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.col6}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    End Date <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="date"
                    name="phases.preCamp.endDate"
                    value={formData.phases.preCamp.endDate}
                    onChange={handleInputChange}
                    style={{
                      ...styles.input,
                      borderColor: errors['phases.preCamp.endDate'] ? colors.error : colors.paleDawn,
                      boxShadow: errors['phases.preCamp.endDate'] ? '0 0 0 1px ' + colors.error : 'none'
                    }}
                  />
                  {errors['phases.preCamp.endDate'] && (
                    <div style={styles.errorText}>
                      <AlertCircle size={14} color={colors.error} />
                      {errors['phases.preCamp.endDate']}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Additional Information</label>
              <textarea
                name="phases.preCamp.additionalInfo"
                value={formData.phases.preCamp.additionalInfo}
                onChange={handleInputChange}
                style={styles.textarea}
                placeholder="Pre-camp description, instructions, etc."
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Packing List</label>
              
              {formData.phases.preCamp.packingItems.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px',
                  animation: 'slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
                  animationDelay: `${index * 0.05}s`
                }}>
                  <input
                    type="text"
                    value={item.item || ''}
                    onChange={(e) => handlePackingItems.update(index, e.target.value)}
                    style={styles.input}
                    placeholder="Enter item"
                  />
                  <button
                    type="button"
                    onClick={() => handlePackingItems.remove(index)}
                    style={{
                      width: '36px',
                      height: '36px',
                      padding: '0',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      backgroundColor: colors.paleDawn,
                      border: 'none',
                      cursor: 'pointer',
                      transition: animations.transition,
                      color: colors.textSecondary
                    }}
                    aria-label="Remove item"
                  >
                    <XCircle size={18} color={colors.textSecondary} />
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={handlePackingItems.add}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  backgroundColor: colors.sunriseRed,
                  color: colors.iceWhite,
                  border: 'none',
                  cursor: 'pointer',
                  transition: animations.transition,
                  gap: '8px',
                  width: '100%',
                  marginTop: '16px'
                }}
              >
                <PlusCircle size={18} /> Add Item
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Camp Phase */}
      <div style={styles.card}>
        <SaveIndicator visible={saveIndicators.mainCamp} />
        
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Main Camp Phase
            </span>
          </h3>
          <InfoTooltip 
            title={phaseDescriptions.mainCamp.title}
            description={phaseDescriptions.mainCamp.description}
            details={phaseDescriptions.mainCamp.details}
          />
        </div>
        
        <div style={styles.row}>
          <div style={styles.col6}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Start Date <span style={styles.required}>*</span>
              </label>
              <input
                type="date"
                name="phases.mainCamp.startDate"
                value={formData.phases.mainCamp.startDate}
                onChange={handleInputChange}
                style={{
                  ...styles.input,
                  borderColor: errors['phases.mainCamp.startDate'] ? colors.error : colors.paleDawn,
                  boxShadow: errors['phases.mainCamp.startDate'] ? '0 0 0 1px ' + colors.error : 'none'
                }}
              />
              {errors['phases.mainCamp.startDate'] && (
                <div style={styles.errorText}>
                  <AlertCircle size={14} color={colors.error} />
                  {errors['phases.mainCamp.startDate']}
                </div>
              )}
            </div>
          </div>
          <div style={styles.col6}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                End Date <span style={styles.required}>*</span>
              </label>
              <input
                type="date"
                name="phases.mainCamp.endDate"
                value={formData.phases.mainCamp.endDate}
                onChange={handleInputChange}
                style={{
                  ...styles.input,
                  borderColor: errors['phases.mainCamp.endDate'] ? colors.error : colors.paleDawn,
                  boxShadow: errors['phases.mainCamp.endDate'] ? '0 0 0 1px ' + colors.error : 'none'
                }}
              />
              {errors['phases.mainCamp.endDate'] && (
                <div style={styles.errorText}>
                  <AlertCircle size={14} color={colors.error} />
                  {errors['phases.mainCamp.endDate']}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Additional Information</label>
          <textarea
            name="phases.mainCamp.additionalInfo"
            value={formData.phases.mainCamp.additionalInfo}
            onChange={handleInputChange}
            style={styles.textarea}
            placeholder="Main camp description, instructions, etc."
          />
        </div>
      </div>

      {/* Post-Camp Phase */}
      <div style={styles.card}>
        <SaveIndicator visible={saveIndicators.postCamp} />
        
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Post-Camp Phase
            </span>
          </h3>
          <InfoTooltip 
            title={phaseDescriptions.postCamp.title}
            description={phaseDescriptions.postCamp.description}
            details={phaseDescriptions.postCamp.details}
          />
        </div>
        
        <div style={styles.switchContainer}>
          <label style={{
            ...styles.switchSlider,
            ...(formData.phases.postCamp.enabled ? styles.switchActive : {})
          }}>
            <input
              type="checkbox"
              style={styles.switchInput}
              checked={formData.phases.postCamp.enabled}
              onChange={() => handleToggle('postCamp', 'enabled')}
            />
            <span style={{
              position: 'absolute',
              content: '""',
              height: '20px',
              width: '20px',
              left: '2px',
              bottom: '2px',
              backgroundColor: 'white',
              borderRadius: '50%',
              transition: 'transform 0.3s ease',
              transform: formData.phases.postCamp.enabled ? 'translateX(20px)' : 'translateX(0)'
            }}></span>
          </label>
          <span style={styles.switchLabel}>
            {formData.phases.postCamp.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {formData.phases.postCamp.enabled && (
          <div style={{ 
            marginTop: '24px',
            animation: 'fadeIn 0.3s ease forwards',
            overflow: 'hidden'
          }}>
            <div style={styles.row}>
              <div style={styles.col6}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Start Date <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="date"
                    name="phases.postCamp.startDate"
                    value={formData.phases.postCamp.startDate}
                    onChange={handleInputChange}
                    style={{
                      ...styles.input,
                      borderColor: errors['phases.postCamp.startDate'] ? colors.error : colors.paleDawn,
                      boxShadow: errors['phases.postCamp.startDate'] ? '0 0 0 1px ' + colors.error : 'none'
                    }}
                  />
                  {errors['phases.postCamp.startDate'] && (
                    <div style={styles.errorText}>
                      <AlertCircle size={14} color={colors.error} />
                      {errors['phases.postCamp.startDate']}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.col6}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    End Date <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="date"
                    name="phases.postCamp.endDate"
                    value={formData.phases.postCamp.endDate}
                    onChange={handleInputChange}
                    style={{
                      ...styles.input,
                      borderColor: errors['phases.postCamp.endDate'] ? colors.error : colors.paleDawn,
                      boxShadow: errors['phases.postCamp.endDate'] ? '0 0 0 1px ' + colors.error : 'none'
                    }}
                  />
                  {errors['phases.postCamp.endDate'] && (
                    <div style={styles.errorText}>
                      <AlertCircle size={14} color={colors.error} />
                      {errors['phases.postCamp.endDate']}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Additional Information</label>
              <textarea
                name="phases.postCamp.additionalInfo"
                value={formData.phases.postCamp.additionalInfo}
                onChange={handleInputChange}
                style={styles.textarea}
                placeholder="Post-camp description, instructions, etc."
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Activities</label>
              
              {formData.phases.postCamp.activityItems.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px',
                  animation: 'slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
                  animationDelay: `${index * 0.05}s`
                }}>
                  <input
                    type="text"
                    value={item.activity || ''}
                    onChange={(e) => handleActivities.update(index, e.target.value)}
                    style={styles.input}
                    placeholder="Enter activity"
                  />
                  <button
                    type="button"
                    onClick={() => handleActivities.remove(index)}
                    style={{
                      width: '36px',
                      height: '36px',
                      padding: '0',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      backgroundColor: colors.paleDawn,
                      border: 'none',
                      cursor: 'pointer',
                      transition: animations.transition,
                      color: colors.textSecondary
                    }}
                    aria-label="Remove activity"
                  >
                    <XCircle size={18} color={colors.textSecondary} />
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={handleActivities.add}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  backgroundColor: colors.sunriseRed,
                  color: colors.iceWhite,
                  border: 'none',
                  cursor: 'pointer',
                  transition: animations.transition,
                  gap: '8px',
                  width: '100%',
                  marginTop: '16px'
                }}
              >
                <PlusCircle size={18} /> Add Activity
              </button>
            </div>
          </div>
        )}
        
        <button
          type="button"
          onClick={() => handleUpdateSection(2)}
          style={{
            marginTop: '20px', 
            width: '100%', 
            padding: '12px',
            backgroundColor: colors.paleDawnLight,
            color: colors.textPrimary,
            fontWeight: '600',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: animations.transition,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            '&:hover': {
              backgroundColor: colors.paleDawn
            }
          }}
        >
          <Save size={16} /> Update Camp Phases
        </button>
      </div>
    </div>
  );

  const renderScheduleStep = () => (
    <div style={styles.section}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: colors.sunriseRed,
          color: colors.iceWhite,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '12px',
          fontWeight: 'bold'
        }}>3</div>
        <div style={{
          fontSize: '18px',
          fontWeight: '600',
          color: colors.textPrimary
        }}>Schedule</div>
      </div>

      {/* Excel Upload Section */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileSpreadsheet size={18} color={colors.textPrimary} />
              Import Schedule Items
            </span>
          </h3>
          <InfoTooltip 
            title="Import from Spreadsheet or PDF"
            description="Upload a CSV, Excel file, or PDF to automatically extract schedule items."
            details={[
              "The system will analyze column headers and content to find dates, times and activities",
              "For PDFs, it will extract text and attempt to identify schedule items",
              "You can edit imported items after importing"
            ]}
          />
        </div>
        
        <FileAnalyzer onScheduleSuggestions={handleScheduleSuggestions} />
      </div>

      {/* Schedule Items */}
      <div style={styles.card}>
        <SaveIndicator visible={saveIndicators.schedule} />
        
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={18} color={colors.textPrimary} />
              Schedule Items
            </span>
          </h3>
          <div style={{ fontSize: '14px', color: colors.textSecondary }}>
            {formData.schedule.length} {formData.schedule.length === 1 ? 'item' : 'items'}
          </div>
        </div>
        
        {/* AI Suggestion if active */}
        {showAISuggestion && (
          <AISuggestion
            suggestion={aiSuggestion}
            onApply={applyAiSuggestion}
            onDismiss={() => setShowAISuggestion(false)}
            loading={aiLoading}
          />
        )}
        
        <div style={styles.scheduleContainer}>
          {formData.schedule.map((item, index) => (
            <div 
              key={index} 
              style={{
                ...styles.scheduleItem,
                ...(expandedScheduleItem === index ? styles.scheduleItemExpanded : {}),
                animation: `fadeIn 0.3s ease forwards ${index * 0.05}s`
              }}
            >
              <div 
                style={{
                  ...styles.scheduleHeader,
                  ...(expandedScheduleItem === index ? styles.scheduleHeaderExpanded : {})
                }}
                onClick={() => {
                  // On mobile, open the drawer
                  if (isMobile) {
                    setActiveScheduleItem(index);
                    setScheduleDrawerOpen(true);
                  } else {
                    // On desktop, expand in-place
                    setExpandedScheduleItem(expandedScheduleItem === index ? null : index);
                  }
                }}
              >
                <div style={{
                  ...styles.scheduleTitle,
                  ...(expandedScheduleItem === index ? styles.scheduleTitleExpanded : {})
                }}>
                  {item.title || 'New Event'}
                  
                  {/* Badges for targeting and repeating */}
                  
                  <div style={{ 
  display: 'flex', 
  gap: '6px', 
  marginLeft: '8px',
  alignItems: 'center',
  height: '20px'
}}>
  {item.forAllGroups ? (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: '16px',
      fontSize: '11px',
      fontWeight: '500',
      gap: '4px',
      backgroundColor: 'rgba(66, 165, 245, 0.1)',
      color: colors.info
    }}>
      <Users size={10} /> All
    </div>
  ) : (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: '16px',
      fontSize: '11px',
      fontWeight: '500',
      gap: '4px',
      backgroundColor: 'rgba(126, 87, 194, 0.1)',
      color: colors.accent1
    }}>
      <Users size={10} /> {item.targetGroupIds.length} groups
    </div>
  )}
                    
                    {item.isRepeating && (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: '16px',
      fontSize: '11px',
      fontWeight: '500',
      gap: '4px',
      backgroundColor: 'rgba(102, 187, 106, 0.1)',
      color: colors.accent3
    }}>
      <Repeat size={10} /> Repeating
    </div>
  )}
</div>
                </div>
                
                <div style={{
                  ...styles.scheduleDate,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {item.isMultiDay ? (
                    <span>{formatDate(item.date)} - {formatDate(item.endDate)}</span>
                  ) : (
                    <span>{formatDate(item.date)}</span>
                  )}
                  {item.startTime && (
                    <span>{formatTime(item.startTime)} - {item.endTime ? formatTime(item.endTime) : ''}</span>
                  )}
                  <div style={{ 
                    marginLeft: '8px', 
                    transform: `rotate(${expandedScheduleItem === index ? '180deg' : '0deg'})`, 
                    transition: 'transform 0.3s ease'
                  }}>
                    <ChevronDown size={16} color={colors.textSecondary} />
                  </div>
                </div>
              </div>
              
              <div style={{
                ...styles.scheduleContent,
                ...(expandedScheduleItem === index ? styles.scheduleContentExpanded : {})
              }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Title <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    value={item.title || ''}
                    onChange={(e) => handleScheduleChange(index, 'title', e.target.value)}
                    placeholder="Event title"
                    style={{
                      ...styles.input,
                      borderColor: errors[`schedule.${index}.title`] ? colors.error : colors.paleDawn,
                      boxShadow: errors[`schedule.${index}.title`] ? '0 0 0 1px ' + colors.error : 'none'
                    }}
                  />
                  {errors[`schedule.${index}.title`] && (
                    <div style={styles.errorText}>
                      <AlertCircle size={14} color={colors.error} />
                      {errors[`schedule.${index}.title`]}
                    </div>
                  )}
                </div>

                {/* Time presets for quick selection */}
                <ScheduleTimePresets 
                  onSelect={(start, end, presetId) => handleTimePreset(index, start, end, presetId)} 
                  activePreset={activeTimePresets[index]}
                  setActivePreset={(presetId) => setActiveTimePresets({...activeTimePresets, [index]: presetId})}
                />

                <div style={styles.multiDayRow}>
                  <div style={styles.switchContainer}>
                    <label style={{
                      ...styles.switchSlider,
                      ...(item.isMultiDay ? styles.switchActive : {})
                    }}>
                      <input
                        type="checkbox"
                        checked={item.isMultiDay}
                        onChange={() => handleMultiDayToggle(index)}
                        style={styles.switchInput}
                      />
                      <span style={{
                        position: 'absolute',
                        content: '""',
                        height: '20px',
                        width: '20px',
                        left: '2px',
                        bottom: '2px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        transition: 'transform 0.3s ease',
                        transform: item.isMultiDay ? 'translateX(20px)' : 'translateX(0)'
                      }}></span>
                    </label>
                    <span style={styles.multiDayLabel}>Multi-day event</span>
                  </div>
                </div>

                <div style={item.isMultiDay ? styles.dateTimeGrid : styles.scheduleGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Start Date <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="date"
                      value={item.date || ''}
                      onChange={(e) => handleScheduleChange(index, 'date', e.target.value)}
                      style={{
                        ...styles.input,
                        borderColor: errors[`schedule.${index}.date`] ? colors.error : colors.paleDawn,
                        boxShadow: errors[`schedule.${index}.date`] ? '0 0 0 1px ' + colors.error : 'none'
                      }}
                    />
                    {errors[`schedule.${index}.date`] && (
                      <div style={styles.errorText}>
                        <AlertCircle size={14} color={colors.error} />
                        {errors[`schedule.${index}.date`]}
                      </div>
                    )}
                  </div>
                  
                  {item.isMultiDay && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        End Date <span style={styles.required}>*</span>
                      </label>
                      <input
                        type="date"
                        value={item.endDate || ''}
                        onChange={(e) => handleScheduleChange(index, 'endDate', e.target.value)}
                        style={{
                          ...styles.input,
                          borderColor: errors[`schedule.${index}.endDate`] ? colors.error : colors.paleDawn,
                          boxShadow: errors[`schedule.${index}.endDate`] ? '0 0 0 1px ' + colors.error : 'none'
                        }}
                      />
                      {errors[`schedule.${index}.endDate`] && (
                        <div style={styles.errorText}>
                          <AlertCircle size={14} color={colors.error} />
                          {errors[`schedule.${index}.endDate`]}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Enhanced time selector with presets */}
                  <TimeSelector
                    value={item.startTime}
                    onChange={(value) => handleScheduleChange(index, 'startTime', value)}
                    label="Start Time"
                  />
                  
                  <TimeSelector
                    value={item.endTime}
                    onChange={(value) => handleScheduleChange(index, 'endTime', value)}
                    label="End Time"
                  />
                </div>
                
                {/* Time Range Slider */}
                {item.startTime && item.endTime && (
                  <TimeRangeSlider 
                    startTime={item.startTime}
                    endTime={item.endTime}
                    onTimeChange={(start, end) => handleTimeRangeChange(index, start, end)}
                  />
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>Location</label>
                  <input
                    type="text"
                    value={item.location || ''}
                    onChange={(e) => handleScheduleChange(index, 'location', e.target.value)}
                    placeholder="Event location"
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description</label>
                  <textarea
                    value={item.description || ''}
                    onChange={(e) => handleScheduleChange(index, 'description', e.target.value)}
                    placeholder="Event description"
                    style={styles.textarea}
                  />
                </div>

                {/* Repeating event options */}
                <div style={styles.multiDayRow}>
  <div style={styles.switchContainer}>
    <label style={{
      ...styles.switchSlider,
      ...(item.isRepeating ? styles.switchActive : {})
    }}>
      <input
        type="checkbox"
        checked={item.isRepeating}
        onChange={() => handleRepeatingToggle(index)}
        style={styles.switchInput}
      />
      <span style={{
        position: 'absolute',
        content: '""',
        height: '20px',
        width: '20px',
        left: '2px',
        bottom: '2px',
        backgroundColor: 'white',
        borderRadius: '50%',
        transition: 'transform 0.3s ease',
        transform: item.isRepeating ? 'translateX(20px)' : 'translateX(0)'
      }}></span>
    </label>
    <span style={styles.multiDayLabel}>Repeating event</span>
  </div>
</div>

{item.isRepeating && (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
    animation: 'fadeIn 0.3s ease forwards'
  }}>
    <div style={styles.formGroup}>
      <label style={styles.label}>Repeat frequency</label>
      <select
        value={item.repeatFrequency || 'daily'}
        onChange={(e) => handleScheduleChange(index, 'repeatFrequency', e.target.value)}
        style={styles.input}
      >
        <option value="daily">Daily</option>
        <option value="weekly">Weekly</option>
      </select>
    </div>

    <div style={styles.formGroup}>
      <label style={styles.label}>
        Repeat Until <span style={styles.required}>*</span>
      </label>
      <input
        type="date"
        value={item.repeatUntil || ''}
        onChange={(e) => handleScheduleChange(index, 'repeatUntil', e.target.value)}
        style={{
          ...styles.input,
          borderColor: errors[`schedule.${index}.repeatUntil`] ? colors.error : colors.paleDawn,
          boxShadow: errors[`schedule.${index}.repeatUntil`] ? '0 0 0 1px ' + colors.error : 'none'
        }}
      />
      {errors[`schedule.${index}.repeatUntil`] && (
        <div style={styles.errorText}>
          <AlertCircle size={14} color={colors.error} />
          {errors[`schedule.${index}.repeatUntil`]}
        </div>
      )}
    </div>
  </div>
)}

                {/* Group targeting */}
                <GroupSelector
  groups={userGroups}
  selectedGroups={item.targetGroupIds}
  onChange={(groupIds) => handleTargetGroupsChange(index, groupIds)}
  isForAll={item.forAllGroups}
  onToggleForAll={(value) => handleForAllGroupsToggle(index, value)}
  groupMembers={groupMembersCache}
/>

                {/* Schedule Item Actions */}
                <div style={styles.scheduleItemActions}>
                  <div style={styles.actionButtonGroup}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyScheduleItem(index);
                      }}
                      style={{
                        ...styles.actionButton,
                        ...styles.copyButton
                      }}
                    >
                      <Copy size={14} /> Duplicate
                    </button>
                    
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Logic for smart time handling
                        setCurrentAiScheduleItem(index);
                        setAiSuggestion("Would you like me to suggest optimal times based on other events in your schedule?");
                        setShowAISuggestion(true);
                      }}
                      style={{
                        ...styles.actionButton,
                        ...styles.smartTimeButton
                      }}
                    >
                      <Zap size={14} /> Smart Time
                    </button>
                  </div>
                  
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeScheduleItem(index);
                    }}
                    style={{
                      ...styles.button,
                      ...styles.dangerButton,
                      padding: '6px 12px',
                      fontSize: '13px'
                    }}
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addScheduleItem}
          style={{
            ...styles.addButton,
            marginTop: '24px'
          }}
        >
          <PlusCircle size={18} /> Add Schedule Item
        </button>
        
        <button
          type="button"
          onClick={() => handleUpdateSection(3)}
          style={{
            marginTop: '24px', 
            width: '100%', 
            padding: '12px',
            backgroundColor: colors.paleDawnLight,
            color: colors.textPrimary,
            fontWeight: '600',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: animations.transition,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            '&:hover': {
              backgroundColor: colors.paleDawn
            }
          }}
        >
          <Save size={16} /> Update Schedule
        </button>
      </div>
    </div>
  );

  const renderScheduleDrawer = () => {
    if (!scheduleDrawerOpen || activeScheduleItem === null) return null;
    
    const item = formData.schedule[activeScheduleItem];
    if (!item) return null;
    
    return (
      <div 
        style={styles.modalOverlay} 
        onClick={() => setScheduleDrawerOpen(false)}
      >
        <div 
          ref={drawerRef}
          style={{
            ...styles.drawerContainer,
            transition: isDrawerDragging ? 'none' : animations.transition
          }} 
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            style={{
              width: '60px',
              height: '6px',
              borderRadius: '3px',
              backgroundColor: colors.paleDawn,
              margin: '12px auto',
              cursor: 'pointer'
            }} 
            onTouchStart={handleDrawerTouchStart}
            onTouchMove={handleDrawerTouchMove}
            onTouchEnd={handleDrawerTouchEnd}
          />
          
          <div style={styles.header}>
            <h2 style={styles.headerTitle}>
              {item.title || 'New Event'}
            </h2>
            <button 
              type="button" 
              onClick={() => setScheduleDrawerOpen(false)}
              style={styles.closeButton}
              aria-label="Close"
            >
              <XCircle size={20} color={colors.textSecondary} />
            </button>
          </div>
          
          <div style={{
            padding: '20px',
            overflowY: 'auto',
            maxHeight: 'calc(90vh - 80px)'
          }}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Title <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={item.title || ''}
                onChange={(e) => handleScheduleChange(activeScheduleItem, 'title', e.target.value)}
                placeholder="Event title"
                style={{
                  ...styles.input,
                  borderColor: errors[`schedule.${activeScheduleItem}.title`] ? colors.error : colors.paleDawn,
                  boxShadow: errors[`schedule.${activeScheduleItem}.title`] ? '0 0 0 1px ' + colors.error : 'none'
                }}
              />
              {errors[`schedule.${activeScheduleItem}.title`] && (
                <div style={styles.errorText}>
                  <AlertCircle size={14} color={colors.error} />
                  {errors[`schedule.${activeScheduleItem}.title`]}
                </div>
              )}
            </div>

            {/* Time presets for quick selection */}
            <ScheduleTimePresets 
              onSelect={(start, end, presetId) => handleTimePreset(activeScheduleItem, start, end, presetId)} 
              activePreset={activeTimePresets[activeScheduleItem]}
              setActivePreset={(presetId) => setActiveTimePresets({...activeTimePresets, [activeScheduleItem]: presetId})}
            />

            <div style={styles.multiDayRow}>
              <div style={styles.switchContainer}>
                <label style={{
                  ...styles.switchSlider,
                  ...(item.isMultiDay ? styles.switchActive : {})
                }}>
                  <input
                    type="checkbox"
                    checked={item.isMultiDay}
                    onChange={() => handleMultiDayToggle(activeScheduleItem)}
                    style={styles.switchInput}
                  />
                  <span style={{
                    position: 'absolute',
                    content: '""',
                    height: '20px',
                    width: '20px',
                    left: '2px',
                    bottom: '2px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    transition: 'transform 0.3s ease',
                    transform: item.isMultiDay ? 'translateX(20px)' : 'translateX(0)'
                  }}></span>
                </label>
                <span style={styles.multiDayLabel}>Multi-day event</span>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Start Date <span style={styles.required}>*</span>
              </label>
              <input
                type="date"
                value={item.date || ''}
                onChange={(e) => handleScheduleChange(activeScheduleItem, 'date', e.target.value)}
                style={{
                  ...styles.input,
                  borderColor: errors[`schedule.${activeScheduleItem}.date`] ? colors.error : colors.paleDawn,
                  boxShadow: errors[`schedule.${activeScheduleItem}.date`] ? '0 0 0 1px ' + colors.error : 'none'
                }}
              />
              {errors[`schedule.${activeScheduleItem}.date`] && (
                <div style={styles.errorText}>
                  <AlertCircle size={14} color={colors.error} />
                  {errors[`schedule.${activeScheduleItem}.date`]}
                </div>
              )}
            </div>
            
            {item.isMultiDay && (
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  End Date <span style={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={item.endDate || ''}
                  onChange={(e) => handleScheduleChange(activeScheduleItem, 'endDate', e.target.value)}
                  style={{
                    ...styles.input,
                    borderColor: errors[`schedule.${activeScheduleItem}.endDate`] ? colors.error : colors.paleDawn,
                    boxShadow: errors[`schedule.${activeScheduleItem}.endDate`] ? '0 0 0 1px ' + colors.error : 'none'
                  }}
                />
                {errors[`schedule.${activeScheduleItem}.endDate`] && (
                  <div style={styles.errorText}>
                    <AlertCircle size={14} color={colors.error} />
                    {errors[`schedule.${activeScheduleItem}.endDate`]}
                  </div>
                )}
              </div>
            )}
            
            <div style={styles.row}>
              <div style={styles.col6}>
                <TimeSelector
                  value={item.startTime}
                  onChange={(value) => handleScheduleChange(activeScheduleItem, 'startTime', value)}
                  label="Start Time"
                />
              </div>
              
              <div style={styles.col6}>
                <TimeSelector
                  value={item.endTime}
                  onChange={(value) => handleScheduleChange(activeScheduleItem, 'endTime', value)}
                  label="End Time"
                />
              </div>
            </div>
            
            {/* Time Range Slider */}
            {item.startTime && item.endTime && (
              <TimeRangeSlider 
                startTime={item.startTime}
                endTime={item.endTime}
                onTimeChange={(start, end) => handleTimeRangeChange(activeScheduleItem, start, end)}
              />
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Location</label>
              <input
                type="text"
                value={item.location || ''}
                onChange={(e) => handleScheduleChange(activeScheduleItem, 'location', e.target.value)}
                placeholder="Event location"
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                value={item.description || ''}
                onChange={(e) => handleScheduleChange(activeScheduleItem, 'description', e.target.value)}
                placeholder="Event description"
                style={styles.textarea}
              />
            </div>
            
            {/* Repeating event options */}
            <div style={styles.multiDayRow}>
              <div style={styles.switchContainer}>
                <label style={{
                  ...styles.switchSlider,
                  ...(item.isRepeating ? styles.switchActive : {})
                }}>
                  <input
                    type="checkbox"
                    checked={item.isRepeating}
                    onChange={() => handleRepeatingToggle(activeScheduleItem)}
                    style={styles.switchInput}
                  />
                  <span style={{
                    position: 'absolute',
                    content: '""',
                    height: '20px',
                    width: '20px',
                    left: '2px',
                    bottom: '2px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    transition: 'transform 0.3s ease',
                    transform: item.isRepeating ? 'translateX(20px)' : 'translateX(0)'
                  }}></span>
                </label>
                <span style={styles.multiDayLabel}>Repeating event</span>
              </div>
            </div>

            {item.isRepeating && (
              <div style={styles.row}>
                <div style={styles.col6}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Repeat frequency</label>
                    <select
                      value={item.repeatFrequency || 'daily'}
                      onChange={(e) => handleScheduleChange(activeScheduleItem, 'repeatFrequency', e.target.value)}
                      style={styles.input}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  </div>
                </div>
                
                <div style={styles.col6}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Repeat Until <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="date"
                      value={item.repeatUntil || ''}
                      onChange={(e) => handleScheduleChange(activeScheduleItem, 'repeatUntil', e.target.value)}
                      style={{
                        ...styles.input,
                        borderColor: errors[`schedule.${activeScheduleItem}.repeatUntil`] ? colors.error : colors.paleDawn,
                        boxShadow: errors[`schedule.${activeScheduleItem}.repeatUntil`] ? '0 0 0 1px ' + colors.error : 'none'
                      }}
                    />
                    {errors[`schedule.${activeScheduleItem}.repeatUntil`] && (
                      <div style={styles.errorText}>
                        <AlertCircle size={14} color={colors.error} />
                        {errors[`schedule.${activeScheduleItem}.repeatUntil`]}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Group target selector for drawer */}
            <GroupSelector
              groups={userGroups}
              selectedGroups={item.targetGroupIds}
              onChange={(groupIds) => handleTargetGroupsChange(activeScheduleItem, groupIds)}
              isForAll={item.forAllGroups}
              onToggleForAll={(value) => handleForAllGroupsToggle(activeScheduleItem, value)}
              groupMembers={groupMembersCache}
            />
            
            {/* Auto-save indicator for mobile drawer */}
            <div style={{marginTop: '20px', textAlign: 'center'}}>
              <SaveIndicator visible={saveIndicators.schedule} text="Automatically saved" />
            </div>
          </div>
          
          <div style={styles.buttonContainer}>
            <button
              type="button"
              onClick={() => {
                removeScheduleItem(activeScheduleItem);
                setScheduleDrawerOpen(false);
              }}
              style={{
                ...styles.button,
                ...styles.dangerButton
              }}
            >
              <Trash2 size={18} /> Remove
            </button>
            <button
              type="button"
              onClick={() => {
                copyScheduleItem(activeScheduleItem);
                setScheduleDrawerOpen(false);
              }}
              style={{
                ...styles.button,
                ...styles.secondaryButton
              }}
            >
              <Copy size={18} /> Duplicate
            </button>
            <button
              type="button"
              onClick={() => setScheduleDrawerOpen(false)}
              style={{
                ...styles.button,
                ...styles.primaryButton
              }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Animation keyframes
  useEffect(() => {
    // Create style element for keyframes
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      @keyframes slideIn {
        0% { transform: translateY(20px); opacity: 0; }
        100% { transform: translateY(0); opacity: 1; }
      }
      
      @keyframes slideUp {
        0% { transform: translateY(100%); }
        100% { transform: translateY(0); }
      }
      
      @keyframes fadeIn {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
      
      @keyframes slideInRight {
        0% { transform: translateX(20px); opacity: 0; }
        100% { transform: translateX(0); opacity: 1; }
      }
      
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
      
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      @keyframes popIn {
        0% { transform: scale(0.9); opacity: 0; }
        70% { transform: scale(1.05); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      
      @keyframes rotate {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      @keyframes aiLoading {
        0% { left: -30%; }
        50% { left: 70%; }
        100% { left: -30%; }
      }
    `;
    document.head.appendChild(styleElement);
    
    // Cleanup on unmount
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Calculate progress width
  const calculateProgressWidth = () => {
    if (currentStep === 1) return '0%';
    if (currentStep === 2) return '50%';
    if (currentStep === 3) return '100%';
    return '0%';
  };

  // Determine if create/update button should be shown
  const showSubmitButton = stepsCompleted[1] && stepsCompleted[2] && currentStep === 3;

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.formContainer}>
        {/* Enhanced mobile view with drawer handle */}
        {isMobile && (
          <div 
            style={{
              width: '60px',
              height: '6px',
              borderRadius: '3px',
              backgroundColor: colors.paleDawn,
              margin: '12px auto',
              cursor: 'pointer'
            }}
            onTouchStart={handleDrawerTouchStart}
            onTouchMove={handleDrawerTouchMove}
            onTouchEnd={handleDrawerTouchEnd}
          />
        )}
        
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>
            {initialData ? 'Edit Camp' : 'Create New Camp'}
          </h2>
          <button 
            type="button" 
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close"
          >
            <XCircle size={24} color={colors.textSecondary} />
          </button>
        </div>

        {/* Progress indicator */}
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: calculateProgressWidth()
              }}
            ></div>
          </div>
          
          {[1, 2, 3].map(step => (
            <div key={step}>
              <div 
                style={{
                  ...styles.progressStep,
                  ...(stepsCompleted[step] ? styles.progressStepCompleted : {}),
                  ...(currentStep === step ? styles.progressStepActive : {})
                }}
                onClick={() => {
                  // Only allow navigation to completed steps or current step
                  if (stepsCompleted[step] || step <= currentStep) {
                    setCurrentStep(step);
                  }
                }}
              >
                {step}
              </div>
              <div 
                style={{
                  ...styles.progressStepLabel,
                  ...(currentStep === step ? styles.progressStepActiveLabel : {})
                }}
              >
                {step === 1 ? 'Basic Info' : step === 2 ? 'Phases' : 'Schedule'}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div 
            style={{
              ...styles.formContent,
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              opacity: 1,
              transform: 'translateX(0)'
            }}
            ref={formContentRef}
          >
            {currentStep === 1 && renderBasicInfoStep()}
            {currentStep === 2 && renderPhasesStep()}
            {currentStep === 3 && renderScheduleStep()}
            
            {formError && (
              <div style={{
                padding: '16px',
                borderRadius: '8px',
                fontSize: '14px',
                marginTop: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                backgroundColor: '#FFEBEE',
                color: colors.error
              }}>
                <AlertCircle size={18} color={colors.error} />
                {formError}
              </div>
            )}
          </div>

          {/* Navigation & Submit Buttons */}
          <div style={styles.buttonContainer}>
            <div>
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={goToPrevStep}
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton
                  }}
                >
                  Back
                </button>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              {currentStep < totalSteps && (
                <button
                  type="button"
                  onClick={goToNextStep}
                  style={{
                    ...styles.button,
                    ...styles.primaryButton
                  }}
                >
                  Next
                </button>
              )}
              
              {showSubmitButton && (
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    ...styles.button,
                    ...styles.successButton,
                    opacity: loading ? 0.7 : 1,
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? (
                    <>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: `2px solid ${colors.iceWhite}`,
                        borderRadius: '50%',
                        borderTopColor: 'transparent',
                        animation: 'rotate 1s linear infinite'
                      }}></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {initialData ? 'Update Camp' : 'Create Camp'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1100,
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: `4px solid ${colors.paleDawnLight}`,
              borderRadius: '50%',
              borderTop: `4px solid ${colors.sunriseRed}`,
              animation: 'spin 1s linear infinite'
            }}></div>
            <div style={{ color: colors.textPrimary, fontSize: '16px', fontWeight: '500' }}>
              Saving camp data...
            </div>
          </div>
        )}
      </div>
      
      {/* Mobile Schedule Drawer */}
      {renderScheduleDrawer()}
    </div>
  );
};

export default EnhancedCampManagementForm;